{
  "gameVersion": "1.5.97",
  "tasks": [
    {
      "name": "action",
      "uses": [
        {
          "description": "Casts an action from the task's assigned actor at their current position. This form is actor-initiated, meaning the actor performs the action as if they chose to use it themselves.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the action to cast. This should match an action defined in the game data."
            }
          ],
          "optional": []
        },
        {
          "description": "Makes a specific actor use an action, optionally targeting a tile coordinate. The specified actor initiates the action as if they chose to perform it. This allows precise control over which actor performs the action and where they target it.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the action to execute. This should match an action defined in the game data."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the actor who will perform the action. This actor must exist in the current zone for this behavior to trigger."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The target tile coordinate where the action should be directed. If not provided, defaults to tile (-1, -1)."
            }
          ]
        },
        {
          "description": "Directly executes an action at a location without being initiated by a specific actor. The action simply happens at the target location rather than being performed by an actor. If a tile coordinate is provided, the action executes there; otherwise, it executes at the task's assigned actor's current position. To use this mode, the second string must be either empty or reference an actor ID that doesn't exist in the current zone.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the action to execute. This should match an action defined in the game data."
            },
            {
              "name": "strings[1]",
              "description": "An actor ID value that must be either an empty string or an invalid/non-existent actor ID to trigger direct execution mode. If this references a valid actor, the task will use actor-initiated behavior instead."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The target tile coordinate where the action should execute. If not provided or set to (-1, -1), the action executes at the task's assigned actor's current position instead."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "actionOnPartyNumActor",
      "uses": [
        {
          "description": "Executes an action at the position of a specific party member, identified by their index in the party roster. The action targets and executes at that party member's current tile location. If the specified party index doesn't exist (index is out of range), this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the action to execute. This action will be executed at the specified party member's position."
            },
            {
              "name": "floats[0]",
              "description": "The zero-based index of the party member in the party roster. For example, 0 refers to the first party member, 1 to the second, and so on."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "applyElement",
      "uses": [
        {
          "description": "Applies an elemental reaction to a specific tile. This causes the tile's terrain or features to react with the specified element, potentially changing the tile's state, creating effects, or triggering element-based interactions.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The element type to apply to the tile. This should be a valid Element enum value (such as fire, ice, lightning, etc.)."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the element should be applied. If not provided, defaults to tile (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "applyElementToArea",
      "uses": [
        {
          "description": "Applies an elemental reaction to a rectangular area of tiles. The element is applied to each tile in the specified area, starting from a top-left corner and covering a width and height. Optionally, a delay increment can be specified to stagger the application timing across tiles, creating a spreading or cascading effect.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The element type to apply to the tiles. This should be a valid Element enum value (such as fire, ice, lightning, etc.)."
            },
            {
              "name": "strings[1]",
              "description": "The size of the rectangular area as a TileCoord, where X represents the width and Y represents the height of the area. For example, '3,2' creates a 3-wide by 2-tall rectangle."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The top-left corner tile coordinate of the rectangular area where elements will be applied. If not provided, defaults to tile (-1, -1)."
            },
            {
              "name": "floats[0]",
              "description": "The delay increment added for each successive tile in the area. This creates a staggered timing effect, with each tile's reaction delayed slightly more than the previous one, producing a spreading visual effect."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "applyElementToZone",
      "uses": [
        {
          "description": "Applies an elemental reaction to the entire current zone, affecting all tiles simultaneously. This is a fast, zone-wide application of an element that can create large-scale environmental effects or state changes across the entire map.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The element type to apply to the zone. This should be a valid Element enum value (such as fire, ice, lightning, etc.)."
            }
          ],
          "optional": [
            {
              "name": "bools[0]",
              "description": "A boolean flag parameter passed to the zone reaction function. Its exact behavior depends on the element and zone reaction implementation."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setTileTerrain",
      "uses": [
        {
          "description": "Changes the terrain type of a specific tile to a new terrain. This modifies the tile's fundamental terrain properties, affecting movement, visuals, and interactions. The change respects terrain change rules unless forced.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the new terrain type to apply to the tile. This should match a valid terrain type defined in the game data."
            },
            {
              "name": "bools[0]",
              "description": "A boolean flag parameter that affects how the terrain change is applied. The exact behavior depends on the terrain change implementation."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate of the tile whose terrain type should be changed."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "script",
      "uses": [
        {
          "description": "Executes a Lua script. This allows running custom Lua code to perform complex logic, manipulate game state, or trigger advanced behaviors that aren't available through standard tasks. The script has access to the game's Lua API and environment.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The Lua script code to execute. This should be valid Lua syntax and can use any functions or variables available in the game's Lua environment."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "consolecommand",
      "uses": [
        {
          "description": "Executes a console command as if it were typed into the game's debug console. This provides access to developer commands and debugging features. After execution, debug draw strings are cleared from the display.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The console command to execute. This should be a valid command recognized by the game's console command handler."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "togglePause",
      "uses": [
        {
          "description": "Toggles the pause state of actors and actions in the current zone. If the game is currently paused, this unpauses it; if unpaused, this pauses it. When paused, actors and actions in the zone are frozen and do not update or execute.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "preventInteraction",
      "uses": [
        {
          "description": "Prevents player interaction for a specified duration. During this time, the player cannot interact with objects, NPCs, or trigger interactive elements. This is useful for cutscenes or scripted sequences where player input should be temporarily disabled.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The duration in seconds to prevent interaction. The interaction block will last for this amount of time before automatically expiring."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "delayActions",
      "uses": [
        {
          "description": "Delays the execution of the next action in the zone by a specified duration. If there is already a delay set, this task will use whichever delay is longer (the existing delay or the new delay). This creates a pause between actions, useful for pacing scripted sequences or combat events.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The delay duration in seconds. The next action will be delayed by at least this amount of time, or longer if a longer delay is already set."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cutsceneMode",
      "uses": [
        {
          "description": "Controls cutscene mode for the current zone. When cutscene mode is enabled, special gameplay restrictions apply and the speed-up enemy turn toggle is disabled. If no boolean is provided, cutscene mode is enabled by default. When cutscene mode is disabled, normal gameplay resumes.",
          "required": [],
          "optional": [
            {
              "name": "bools[0]",
              "description": "Whether to enable or disable cutscene mode. True enables cutscene mode, false disables it. If not provided, defaults to true (enabling cutscene mode)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "timeSpeed",
      "uses": [
        {
          "description": "Sets the game's time speed multiplier. This controls how fast time passes in the game world, affecting day/night cycles, timed events, and other time-dependent systems. A value of 1.0 is normal speed, values greater than 1.0 speed up time, and values less than 1.0 slow it down.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The time speed multiplier. For example, 2.0 makes time pass twice as fast, 0.5 makes it pass at half speed, and 1.0 is normal speed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setFactionHostile",
      "uses": [
        {
          "description": "Sets whether a faction is hostile or friendly. This changes the faction's relationship with the player and other factions, affecting combat behavior and interactions. When set to hostile, the faction's members will attack the player; when set to friendly, they will not. An optional tile coordinate parameter affects how the hostility is applied.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the faction whose hostility status should be changed. This should match a faction defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The hostility value. Values of 1.0 or greater set the faction to hostile, while values less than 1.0 set the faction to friendly."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "An optional tile coordinate parameter. When provided (not (-1, -1)), the X coordinate value affects how the hostility change is applied. The exact behavior depends on the setFactionHostile implementation."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setFactionAI",
      "uses": [
        {
          "description": "Changes the AI behavior of a faction. This controls how members of the faction make decisions, choose targets, and act during their turns. Different AI types can make factions more aggressive, defensive, passive, or follow custom behavior patterns.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the faction whose AI should be changed. This should match a faction defined in the game data."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the AI type to assign to the faction. This should match a valid AI type defined in the game data."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "killFaction",
      "uses": [
        {
          "description": "Incapacitates all actors belonging to a specified faction in the current zone. Each actor in the faction becomes incapacitated, causing them to fall and become unable to act. This is a zone-wide effect that affects all members of the faction simultaneously.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the faction whose members should be incapacitated. All actors in the current zone with this faction will become incapacitated."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeFaction",
      "uses": [
        {
          "description": "Completely removes all actors belonging to a specified faction from the current zone. Unlike killFaction which incapacitates actors, this task removes them entirely from the game world. If a special zone exists, actors from that faction are also removed from the special zone. This is a permanent removal that deletes the actors rather than just disabling them.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the faction whose members should be removed. All actors in the current zone (and special zone if present) with this faction will be completely removed from the game."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "changeFaction",
      "uses": [
        {
          "description": "Changes all actors from one faction to another faction. This reassigns the faction membership of actors, affecting their allegiances, hostilities, and behaviors. All actors currently belonging to the first faction will be moved to the second faction.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the source faction. All actors currently in this faction will be changed to the target faction."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the target faction. All actors from the source faction will be reassigned to this faction."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveToNewZone",
      "uses": [
        {
          "description": "Transitions the game to a new zone, loading the specified zone and positioning the player at a target location. This handles the zone change, including loading the new zone data and setting up the player's starting position. A brief interaction delay is automatically applied to prevent immediate input after the zone transition.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the zone to move to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "A numeric parameter passed to the zone transition function. The exact purpose depends on the moveToNewZone implementation."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the player should be positioned in the new zone. If not provided, defaults to tile (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel",
      "uses": [
        {
          "description": "Initiates travel to another zone when used in combat mode. This prompts the player with a dialog asking if they want to flee to the destination zone. The tile coordinates are passed as parameters to the flee dialog. If not in combat mode or if in action mode, this use case may not apply.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the destination zone to flee to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The direction the party will be facing, in degrees."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate used in the flee prompt parameters, passed as 'X,Y' coordinates. If not provided, defaults to tile (-1, -1), which would be passed as '-1,-1'."
            }
          ]
        },
        {
          "description": "Initiates direct travel to another zone when used outside of combat mode (but not during action mode). The player is moved to the specified zone at the target tile coordinate, with control over whether autosave occurs during the transition. If in combat mode or action mode, this use case may not apply.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the destination zone to travel to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The direction the party will be facing, in degrees."
            },
            {
              "name": "bools[0]",
              "description": "Controls autosave behavior during travel. When true, autosave is disabled for this travel; when false, autosave is enabled."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the player should arrive in the destination zone. If not provided, defaults to tile (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_point",
      "uses": [
        {
          "description": "When in combat, prompts the player to flee. If accepted, moves the party to a new zone at the specified travel point.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the destination zone to flee to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The travel point to move to."
            }
          ],
          "optional": []
        },
        {
          "description": "Moves the party to a new zone at the specified travel point.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the destination zone to travel to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The travel point to move to."
            }
          ],
          "optional": [
            {
              "name": "bools[0]",
              "description": "Controls autosave behavior during travel. When true, autosave is disabled for this travel; when false, autosave is enabled. If not provided, defaults to true (autosave enabled)."
            },
            {
              "name": "bools[1]",
              "description": "When true, sets a flag indicating the zone is being entered via console mode. This may affect zone initialization or behavior. If not provided or false, normal zone entry occurs."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_point_g",
      "uses": [
        {
          "description": "When in combat, prompts the player to flee. If accepted, moves the party to a new zone at the specified travel point. The zone name is looked up from the global variable specified in the first string parameter.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that contains the destination zone ID. The value of this global variable should be a valid zone ID."
            },
            {
              "name": "floats[0]",
              "description": "The travel point to move to."
            }
          ],
          "optional": []
        },
        {
          "description": "Moves the party to a new zone at the specified travel point. The zone name is looked up from the global variable specified in the first string parameter.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that contains the destination zone ID. The value of this global variable should be a valid zone ID."
            },
            {
              "name": "floats[0]",
              "description": "The travel point to move to."
            }
          ],
          "optional": [
            {
              "name": "bools[0]",
              "description": "Controls autosave behavior during travel. When true, autosave is disabled for this travel; when false, autosave is enabled. If not provided, defaults to true (autosave enabled)."
            },
            {
              "name": "bools[1]",
              "description": "When true, sets a flag indicating the zone is being entered via console mode. This may affect zone initialization or behavior. If not provided or false, normal zone entry occurs."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_leaveLocation",
      "uses": [
        {
          "description": "Leaves the current location when in combat mode, prompting the player with a flee confirmation dialog. If a specific tile coordinate is provided and the first boolean is false, that coordinate is used for the leave destination; otherwise, the origin coordinate (0,0) is used. The dialog is triggered with a specific confirmation callback for leaving locations during combat.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Controls whether to use a custom tile coordinate or default to origin. When false and no tile coordinate is provided, the destination defaults to tile (0,0). When true, the provided tile coordinate is used."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The destination tile coordinate for leaving the location. If not provided, defaults to tile (-1,-1), which may then be changed to (0,0) based on bools[0]."
            }
          ]
        },
        {
          "description": "Leaves the current location when outside of combat mode, immediately transitioning away from the current location. The departure uses the specified tile coordinate (or a default based on the first boolean) and controls autosave behavior. This is a direct leave action without a confirmation dialog.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Controls whether to use a custom tile coordinate or default to origin. When false and no tile coordinate is provided, the destination defaults to tile (0,0). When true, the provided tile coordinate is used."
            },
            {
              "name": "bools[1]",
              "description": "Controls autosave behavior when leaving. When true, autosave is disabled; when false, autosave is enabled."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The destination tile coordinate for leaving the location. If not provided, defaults to tile (-1,-1), which may then be changed to (0,0) based on bools[0]."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_gVars",
      "uses": [
        {
          "description": "Travels to the player's previous zone using coordinates and zone name stored in global variables, when in combat mode. This prompts a flee dialog to return to the last known player position. The zone name is read from the 'playerPrevZone' global variable, and the coordinates are read from 'playerX' and 'playerY' global variables.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The direction the party will be facing, in degrees."
            }
          ],
          "optional": []
        },
        {
          "description": "Travels to the player's previous zone using coordinates and zone name stored in global variables, when outside of combat mode. The player is moved to the position stored in 'playerX' and 'playerY' global variables within the zone specified by 'playerPrevZone' global variable. This provides a way to return to a saved player position.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The direction the party will be facing, in degrees."
            },
            {
              "name": "bools[0]",
              "description": "Controls autosave behavior during travel. When true, autosave is disabled; when false, autosave is enabled."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_noSave",
      "uses": [
        {
          "description": "Initiates travel to another zone. This is similar to the standard travel task but specifically designed for scenarios where autosave should not occur. When in combat mode, prompts a flee dialog.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the destination zone to flee to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The direction the party will be facing, in degrees."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The target tile coordinate, used in the flee prompt parameters. If not provided, defaults to tile (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_procedural",
      "uses": [
        {
          "description": "Creates and travels to a procedurally generated world. The procedural world is generated with a starting zone template and an end destination zone, creating a series of connected zones between them. The return coordinates determine where the player returns to if they leave the procedural world. These coordinates can be explicitly provided via tile coordinates or automatically calculated based on task area bounds.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the zone template to use as the basis for the procedural world generation. This defines the style and content of the generated zones."
            },
            {
              "name": "strings[1]",
              "description": "Comma-separated values specifying the end destination zone and optionally a bottom exit override. Format: 'endZoneID' or 'endZoneID,bottomExitOverride'. Spaces are automatically stripped from this value before parsing."
            },
            {
              "name": "bools[0]",
              "description": "Controls autosave behavior when entering the procedural world. When true, autosave is disabled; when false, autosave is enabled."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The return coordinates (X,Y) where the player will return to if they leave the procedural world. If not provided, defaults to (-1, -1). If bools[1] is true, these coordinates are overridden with calculated values based on the task area."
            },
            {
              "name": "bools[1]",
              "description": "When true, automatically calculates the return coordinates based on the task's area bounds (centered horizontally at the bottom edge plus one tile). When false, uses the provided tile coordinates. If not provided, defaults to false."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_procedural_point",
      "uses": [
        {
          "description": "Creates and travels to a procedurally generated world with a point-based entry system. Similar to travel_procedural, but allows specifying an integer point value that controls some aspect of the procedural generation or entry. The procedural world is generated with a starting zone template and an end destination zone.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the zone template to use as the basis for the procedural world generation. This defines the style and content of the generated zones."
            },
            {
              "name": "strings[1]",
              "description": "Comma-separated values specifying the end destination zone and optionally a bottom exit override. Format: 'endZoneID' or 'endZoneID,bottomExitOverride'. Spaces are automatically stripped from this value before parsing."
            },
            {
              "name": "bools[0]",
              "description": "Controls autosave behavior when entering the procedural world. When true, autosave is disabled; when false, autosave is enabled."
            },
            {
              "name": "floats[0]",
              "description": "An integer point value (cast from float) passed to the procedural world creation. This may control the entry point, difficulty level, or other generation parameters specific to point-based procedural worlds."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The return coordinates (X,Y) where the player will return to if they leave the procedural world. If not provided, defaults to (-1, -1). If bools[1] is true, these coordinates are overridden with calculated values based on the task area."
            },
            {
              "name": "bools[1]",
              "description": "When true, automatically calculates the return coordinates based on the task's area bounds (centered horizontally at the bottom edge plus one tile). When false, uses the provided tile coordinates. If not provided, defaults to false."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_setReturnPath",
      "uses": [
        {
          "description": "Initiates travel to another zone when in combat mode, prompting a flee dialog. This is similar to standard travel but associated with setting a return path for later navigation.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the destination zone to flee to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "An integer value (cast from float) passed to the flee dialog, likely controlling flee cost or other flee parameters."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The target tile coordinate, used in the flee prompt parameters. If not provided, defaults to tile (-1, -1)."
            }
          ]
        },
        {
          "description": "Travels to another zone while setting a return path that can be used to navigate back to the current location. An autosave is performed before the travel. The return path coordinates can be explicitly provided or automatically calculated based on the task area. This allows creating a two-way travel connection where the player can later return to this exact spot.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the destination zone to travel to. This should match a zone defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "A timing parameter controlling the zone transition speed or delay."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the player should arrive in the destination zone. If not provided, defaults to tile (-1, -1). Note: This is separate from the return path coordinates."
            },
            {
              "name": "bools[0]",
              "description": "When true, automatically calculates the return path coordinates based on the task's area bounds (centered horizontally at the bottom edge plus one tile). When false or not provided, the return path coordinates default to (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "returnPath",
      "uses": [
        {
          "description": "Returns to a previously set zone using a return path when in combat mode. This prompts a flee dialog to navigate back to the specified zone. If the zone string is empty, this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the zone to return to, typically set by a previous travel_setReturnPath task. If this is an empty string, the task does nothing."
            },
            {
              "name": "floats[0]",
              "description": "An integer value (cast from float) passed to the flee dialog, likely controlling flee cost or other flee parameters."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The target tile coordinate, used in the flee prompt parameters. If not provided, defaults to tile (-1, -1)."
            }
          ]
        },
        {
          "description": "Returns to a previously set zone using a return path when outside of combat mode. An autosave is performed before the return travel. The player is moved to the specified zone at the target coordinates. If the zone string is empty, this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the zone to return to, typically set by a previous travel_setReturnPath task. If this is an empty string, the task does nothing."
            },
            {
              "name": "floats[0]",
              "description": "A timing parameter controlling the zone transition speed or delay."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the player should arrive in the return zone. If not provided, defaults to tile (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "travel_g",
      "uses": [
        {
          "description": "Travels to the last procedural zone entry point when in combat mode, prompting a flee dialog. The zone ID is retrieved from the 'lastProcZoneEntry' global variable, which typically stores the entry point to a procedurally generated world.",
          "required": [
            {
              "name": "floats[0]",
              "description": "An integer value (cast from float) passed to the flee dialog, likely controlling flee cost or other flee parameters."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The target tile coordinate, used in the flee prompt parameters. If not provided, defaults to tile (-1, -1)."
            }
          ]
        },
        {
          "description": "Travels to the last procedural zone entry point when outside of combat mode. The zone ID is retrieved from the 'lastProcZoneEntry' global variable. An autosave is performed before the travel. This provides a way to return to the entry point of a procedurally generated world.",
          "required": [
            {
              "name": "floats[0]",
              "description": "A timing parameter controlling the zone transition speed or delay."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the player should arrive in the destination zone. If not provided, defaults to tile (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVar",
      "uses": [
        {
          "description": "Sets a global variable to a string value. Global variables persist across zones and game sessions (when saved) and can be used to track quest progress, player choices, or other game state. The variable is identified by name and assigned the specified string value.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to set. This identifier is used to retrieve the variable's value later."
            },
            {
              "name": "strings[1]",
              "description": "The string value to assign to the global variable. This must be a non-empty string."
            }
          ],
          "optional": []
        },
        {
          "description": "Sets a global variable to a numeric value. Global variables persist across zones and game sessions (when saved) and can be used to track counters, scores, or other numeric game state. The variable is identified by name and assigned the specified float value (stored as a string internally).",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to set. This identifier is used to retrieve the variable's value later."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to assign to the global variable. This will be converted to a string for storage."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVar_math",
      "uses": [
        {
          "description": "Sets a global variable to the result of a mathematical formula calculation. The formula is evaluated with the task's actor as both the source and target, producing an integer result. The variable is first initialized to '0', then the calculated value is added to it via the modGlobalVar function. This allows for complex calculations involving actor stats, game variables, and mathematical operations.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to set with the calculated result."
            },
            {
              "name": "strings[1]",
              "description": "The formula string to evaluate. This should be a valid formula expression that can reference actor values, global variables, and use mathematical operators. The result is cast to an integer."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVar_toOtherGlobalVar",
      "uses": [
        {
          "description": "Copies the value from one global variable to another global variable. This reads the string value from the source global variable and assigns it to the destination global variable, effectively creating a duplicate or backup of the value.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the destination global variable that will receive the copied value."
            },
            {
              "name": "strings[1]",
              "description": "The name of the source global variable whose value will be copied. The value is retrieved as a string."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarToZoneProperty",
      "uses": [
        {
          "description": "Sets a global variable to the value of a property from the current zone. This reads a property value from the zone object (such as zone ID, name, or other zone attributes) and stores it in a global variable. If the current zone is null, this task does nothing. Achievement checks are bypassed when setting the variable.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that will receive the zone property value."
            },
            {
              "name": "strings[1]",
              "description": "The name of the zone property to read. This should match a valid property or field name on the Zone class (such as 'ID', 'zoneName', etc.)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVar_toPartyActorID",
      "uses": [
        {
          "description": "Sets a global variable to the actor ID of a specific party member. The party member is identified by their zero-based index in the party roster. If the specified index is out of range (no party member exists at that position), this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that will receive the party member's actor ID."
            },
            {
              "name": "floats[0]",
              "description": "The zero-based index of the party member whose actor ID should be retrieved. For example, 0 is the first party member, 1 is the second, and so on."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVar_toActorID",
      "uses": [
        {
          "description": "Sets a global variable to the ID of the task's assigned actor. The actor ID is taken from the task's actorID field, which is typically set when the task is created or triggered by an actor. If the task has no assigned actor (empty actor ID), this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that will receive the task actor's ID."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarWithCommas",
      "uses": [
        {
          "description": "Sets a global variable to a comma-separated concatenation of multiple string values. All strings after the first are joined together with commas between them, creating a single comma-delimited string value. This is useful for setting a global variable value that contains commas, as setGlobalVar would only take the value up until the first comma when used in a task string.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that will receive the concatenated comma-separated string."
            }
          ],
          "optional": [
            {
              "name": "strings[1+]",
              "description": "The string values to concatenate with commas. All strings from index 1 onward are joined together with comma separators. If no additional strings are provided, the variable is set to an empty string."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "clearGlobalVar",
      "uses": [
        {
          "description": "Clears a global variable, removing it from the game state. This deletes the variable entirely rather than just setting it to an empty or zero value. After clearing, the variable will no longer exist until it is set again.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to clear and remove from the game state."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modGlobalVar",
      "uses": [
        {
          "description": "Modifies a global variable by adding a numeric value to its current value. If the variable doesn't exist, it is created with a value of 0 before the modification is applied. If the variable exists but contains a non-numeric value, it will error. This is useful for incrementing counters, scores, or other numeric game state.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to modify."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to add to the global variable. Can be negative to subtract from the variable."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modNodeIDGlobalVar_1",
      "uses": [
        {
          "description": "Modifies a global variable named after the current dialog node ID by adding 1 to its value. The variable name is taken from the task's currentDialogNodeID field, which is set by the dialog system. This is typically used to track progression through dialog trees or count how many times a dialog node has been visited.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modNodeIDGlobalVar_999",
      "uses": [
        {
          "description": "Modifies a global variable named after the current dialog node ID by adding 999 to its value. The variable name is taken from the task's currentDialogNodeID field. This large increment is typically used to mark a dialog node as completed or to trigger specific dialog branching logic based on high values.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "appendToGlobalVar",
      "uses": [
        {
          "description": "Appends a calculated numeric value to an existing global variable's string value. The formula is evaluated and its result is converted to a string, then concatenated to the end of the current variable value. This allows building up strings that contain calculated numbers or combining text with computed values.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to append to. If the variable doesn't exist, it will be created with an empty string before appending."
            },
            {
              "name": "strings[1]",
              "description": "The formula string to evaluate. The calculated result will be converted to a string and appended to the variable's current value."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["appendToGlobalVar_math"]
    },
    {
      "name": "appendToGlobalVar_string",
      "uses": [
        {
          "description": "Appends a string value to an existing global variable's value. The second string is concatenated to the end of the variable's current value, allowing you to build up longer strings or add text incrementally.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to append to. If the variable doesn't exist, it will be created with an empty string before appending."
            },
            {
              "name": "strings[1]",
              "description": "The string value to append to the variable's current value."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "appendToGlobalVar_zoneID",
      "uses": [
        {
          "description": "Appends the current zone's ID to an existing global variable's value. The zone ID is retrieved from the current zone and concatenated to the variable's value. This is useful for tracking which zones have been visited or building zone-specific variable names.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to append to. The current zone's ID will be concatenated to this variable's current value."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "appendToGlobalVar_locID",
      "uses": [
        {
          "description": "Appends the current location's ID to an existing global variable's value. The location ID is retrieved from the current location and concatenated to the variable's value. If there is no current location, this task does nothing. This is useful for tracking location visits or building location-specific data.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to append to. The current location's ID will be concatenated to this variable's current value."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setNodeIDGlobalVar_1",
      "uses": [
        {
          "description": "Sets a global variable named after the current dialog node ID to the value 1, but only if it's not already 1. The variable name is taken from the task's currentDialogNodeID field. This is typically used to mark a dialog node as visited or to trigger one-time dialog logic.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarTo0",
      "uses": [
        {
          "description": "Sets a global variable to the numeric value 0. This is useful for resetting counters or initializing numeric variables to a known state.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to set to 0."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarToTalkerID",
      "uses": [
        {
          "description": "Sets a global variable to the ID of the actor the player is currently talking to. The talker ID is retrieved from the input manager's currentActorTalkingTo field. This is useful for tracking which NPC triggered a conversation or for creating actor-specific quest variables.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that will receive the talking actor's ID."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarWithTalkerID",
      "uses": [
        {
          "description": "Sets a global variable with a name that combines a base name and the current talker's ID. The variable name is formed by concatenating the base name with the talker's actor ID. The value can be either a string or a numeric value. This allows creating per-NPC variables like 'queststate_npcName' where the NPC name is dynamically included.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The base name for the global variable. The current talker's ID will be appended to this to form the full variable name."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The string value to set. If provided and non-empty, this value is used instead of floats[0]."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to set. This is used only if strings[1] is not provided or is empty. Defaults to 0 if neither is provided."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarToLocID",
      "uses": [
        {
          "description": "Sets a global variable to the ID of the current location. If there is no current location, this task does nothing. This is useful for tracking which location the player is in or for saving location state.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable that will receive the current location's ID."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarToPartyActorNum",
      "uses": [
        {
          "description": "Sets a global variable to a numeric value representing a party actor number or index. Despite the name suggesting it retrieves a party actor number, this task simply sets the variable to the provided float value.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to set."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to assign to the global variable."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarPlusLocID",
      "uses": [
        {
          "description": "Sets a global variable with a name that combines a base name and the current location's ID. The variable name is formed by concatenating the base name with the location ID, allowing for location-specific variables.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The base name for the global variable. The current location's ID will be appended to this to form the full variable name."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to assign to the combined variable name."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarPlusLocID_math",
      "uses": [
        {
          "description": "Sets a global variable (with a name combining a base name and the current location's ID) to the result of a formula calculation. The variable is first initialized to 0, then the calculated value is added to it. This allows for complex calculations involving location-specific variables.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The base name for the global variable. The current location's ID will be appended to this to form the full variable name."
            },
            {
              "name": "strings[1]",
              "description": "The formula string to evaluate. The result (cast to integer) will be set as the variable's value."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarPlusZoneID",
      "uses": [
        {
          "description": "Sets a global variable with a name that combines a base name and the current zone's ID. The variable name is formed by concatenating the base name with the zone ID, allowing for zone-specific variables.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The base name for the global variable. The current zone's ID will be appended to this to form the full variable name."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to assign to the combined variable name."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modFame",
      "uses": [
        {
          "description": "Modifies the player's fame of a certain type with a visual flourish. The fame value is stored in the \"fame_<fameType>\" global variable. Default fame types include: \"explore\", \"trade\", \"combat\", \"piracy\". If you want to add a new type of fame, be sure to include icon images in your mod called \"fame_<fameType>.png\" and \"fame_<fameType>_outlined.png\" or else the game will crash.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The type of fame. Usually one of: \"explore\", \"trade\", \"combat\", \"piracy\""
            },
            {
              "name": "floats[0]",
              "description": "The amount to add to (or subtract from, if negative) the current fame value."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "goToNodeID_plus_GlobalVar",
      "uses": [
        {
          "description": "Navigates to a dialog node with an ID formed by combining the current dialog node ID with a global variable's value. The global variable name is the same as the current dialog node ID. This allows for dynamic dialog branching based on stored state.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enableUI",
      "uses": [
        {
          "description": "Enables the game's UI elements that may have been hidden. This shows the quickslots, character stats, and other UI components, restoring normal UI visibility for gameplay.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "disableUI",
      "uses": [
        {
          "description": "Disables and hides the game's UI elements. This hides the quickslots, character stats, and small tab windows, creating a cleaner screen for cutscenes or special gameplay moments. The UI remains functional but invisible.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "unlockHelpGem",
      "uses": [
        {
          "description": "Unlocks and enables the help gem feature, making it immediately usable. The help gem provides in-game assistance or hints to the player. This task both unlocks the feature and enables it, setting it to be ready for immediate use.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enableHelpGem",
      "uses": [
        {
          "description": "Enables the help gem feature, allowing the player to use it. This assumes the help gem has already been unlocked. When enabled, the player can access help gem functionality.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "disableHelpGem",
      "uses": [
        {
          "description": "Disables the help gem feature, preventing the player from using it. The help gem is also set to be unusable by setting a negative timer value, ensuring it remains disabled until explicitly re-enabled.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "save",
      "uses": [
        {
          "description": "Creates a named save file. If the save name already exists, a '+' character is appended until a unique name is found. This allows creating multiple save points without overwriting existing saves.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The name for the save file. If provided and non-empty, creates a named save with this name (adding '+' suffixes if needed to avoid conflicts). If not provided or empty, performs an autosave instead."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "quietsave",
      "uses": [
        {
          "description": "Performs an autosave without any user-facing notifications or messages. This is a silent save operation that bypasses hardcore mode restrictions, allowing saves even in modes where manual saving is normally disabled.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "heal",
      "uses": [
        {
          "description": "Performs a full heal on the entire party, restoring all party members' HP and status. This is triggered when no heal amount is specified, providing a complete restoration for the whole party.",
          "required": [],
          "optional": []
        },
        {
          "description": "Heals a specific actor by a specified amount of HP. The actor is identified by their ID, and the heal amount can be specified (with 0 being treated as a full heal of 9999 HP). If the specified actor doesn't exist in the current zone, this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to heal. Must be a non-empty actor ID that exists in the current zone."
            },
            {
              "name": "floats[0]",
              "description": "The amount of HP to restore. If set to 0, automatically becomes 9999 (full heal). Otherwise, restores the specified amount."
            }
          ],
          "optional": []
        },
        {
          "description": "Heals the task's assigned actor by a specified amount of HP. The heal amount can be specified (with 0 being treated as a full heal of 9999 HP). This form is used when the actor ID string is empty, causing the heal to target the task's own actor.",
          "required": [
            {
              "name": "strings[0]",
              "description": "Must be an empty string to trigger this use case. A non-empty value would heal that actor instead."
            },
            {
              "name": "floats[0]",
              "description": "The amount of HP to restore. If set to 0, automatically becomes 9999 (full heal). Otherwise, restores the specified amount."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "healparty",
      "uses": [
        {
          "description": "Performs a full heal on the entire party, restoring all party members' HP and status effects. This provides a complete restoration for all party members simultaneously.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "fullrestore",
      "uses": [
        {
          "description": "Performs a complete restoration of the entire party, healing all HP and removing all negative status effects. This is the most comprehensive party heal, fully restoring the party to perfect condition.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "revive",
      "uses": [
        {
          "description": "Revives an incapacitated actor, restoring them to life with a specified amount of HP. Optionally, the revived actor can also be cured of all status effects. The heal amount of 0 is treated as a full heal of 9999 HP. If the specified actor doesn't exist, this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to revive. This actor will be brought back from incapacitation."
            },
            {
              "name": "floats[0]",
              "description": "The amount of HP to restore when reviving. If set to 0, automatically becomes 9999 (full heal). Otherwise, revives with the specified HP amount."
            },
            {
              "name": "bools[0]",
              "description": "When true, also cures all status effects from the revived actor. When false, only revives without removing status effects."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "fx",
      "uses": [
        {
          "description": "Adds a visual effect to the effects queue without specifying a position. The effect will be displayed at a default location. Use this form when only the effect ID is needed.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display."
            }
          ],
          "optional": []
        },
        {
          "description": "Adds a visual effect at a specific actor's position. The effect will appear centered on the actor, following their location. If the actor doesn't exist, the effect is not displayed.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the actor at whose position the effect should appear. Must be a valid, non-empty actor ID."
            }
          ],
          "optional": []
        },
        {
          "description": "Adds a visual effect at the task actor's position. This is triggered when the second string is empty and no tile coordinate is provided. The effect appears at the task's assigned actor's location.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display."
            },
            {
              "name": "strings[1]",
              "description": "Must be an empty string to trigger this use case."
            }
          ],
          "optional": []
        },
        {
          "description": "Adds a visual effect at the camera's position. This is triggered when the second string is empty, no tile coordinate is provided, and there is no task actor. The effect appears at the current camera location.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display."
            },
            {
              "name": "strings[1]",
              "description": "Must be an empty string to trigger this use case."
            }
          ],
          "optional": []
        },
        {
          "description": "Adds a visual effect with a specific color at a tile coordinate. The effect is displayed at the specified tile position with white coloring. This form requires both a tile coordinate and the color flag to be enabled.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display."
            },
            {
              "name": "strings[1]",
              "description": "Must be an empty string to allow tile-based positioning."
            },
            {
              "name": "bools[0]",
              "description": "Must be true to enable the white color effect."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the effect should appear."
            }
          ]
        },
        {
          "description": "Adds a visual effect at a tile coordinate. The effect is displayed at the specified tile position without color modification. This form requires a tile coordinate but no color flag.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display."
            },
            {
              "name": "strings[1]",
              "description": "Must be an empty string to allow tile-based positioning."
            },
            {
              "name": "bools[0]",
              "description": "Must be false or not provided for standard effect display without color modification."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the effect should appear."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "sfx",
      "uses": [
        {
          "description": "Plays a sound effect. The sound is played through the sound manager using the specified sound name identifier.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The name of the sound effect to play. Should match a sound defined in the game's sound system. If not provided, no sound is played."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "fxOnTalker",
      "uses": [
        {
          "description": "Adds a visual effect at the position of the actor the player is currently talking to. The effect appears centered on the talking NPC's location. If there is no current talking actor or the actor doesn't exist, no effect is displayed.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display at the talker's position. If not provided, no effect is displayed."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "fxOnParty",
      "uses": [
        {
          "description": "Adds a visual effect at the position of each party member simultaneously. The same effect appears on all party members, creating a coordinated visual display across the entire party.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to display on each party member. If not provided, no effects are displayed."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "damageNumber",
      "uses": [
        {
          "description": "Displays a floating damage number (or text) above the player's position. The text moves upward and fades out over time. The display duration can be adjusted with a timing modifier.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The text to display as a damage number. Can be actual damage values or any other text."
            },
            {
              "name": "floats[0]",
              "description": "A timing modifier added to the base duration of 2 seconds. Positive values make the text display longer, negative values make it shorter."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "damageNumber_worldTime",
      "uses": [
        {
          "description": "Displays the current in-game world time as a floating damage number above the player's position. The time is formatted as hours:minutes and moves upward while fading out. The display duration can be adjusted with a timing modifier.",
          "required": [
            {
              "name": "floats[0]",
              "description": "A timing modifier added to the base duration of 2 seconds. Positive values make the time display longer, negative values make it shorter."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "animate",
      "uses": [
        {
          "description": "Triggers an animation on an actor and optionally sets a busy timer. The animation plays on either a specified actor or the task's assigned actor. If a busy duration is provided, the actor is also stunned (preventing actions) for that duration.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the animation to play. Should match an animation defined for the actor's animator."
            },
            {
              "name": "floats[0]",
              "description": "The busy timer duration in seconds. If greater than 0, the actor is stunned and prevented from acting for this duration. If 0 or negative, no busy timer is set."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The ID of the actor to animate. If provided and non-empty, that actor performs the animation. If empty or not provided, the task's assigned actor performs the animation."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "textOverlay",
      "uses": [
        {
          "description": "Displays an overlay text message on the screen at a specified position. The text can be displayed with a transparency level and positioning control. Additional flags control whether the text can be dismissed or auto-revealed character by character.",
          "required": [
            {
              "name": "bools[0]",
              "description": "When true, the text cannot be dismissed by the player (inverted internally). When false, the player can dismiss the text."
            },
            {
              "name": "bools[1]",
              "description": "When true, the text does not auto-reveal character by character (inverted internally). When false, the text reveals gradually."
            }
          ],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The text message to display. If not provided, displays an empty string."
            },
            {
              "name": "floats[0]",
              "description": "The transparency/alpha level of the text overlay, cast to a byte value (0-255 range). If not provided, defaults to 0."
            },
            {
              "name": "tileCoords[0]",
              "description": "The X and Y position for the text overlay. If not provided, both X and Y default to 0."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "textOverlay_tween",
      "uses": [
        {
          "description": "Displays an overlay text message with a tweening animation effect. The text smoothly transitions into view at the specified position with the given transparency level. This creates a more polished visual appearance compared to the standard text overlay.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The text message to display. If not provided, displays an empty string."
            },
            {
              "name": "floats[0]",
              "description": "The transparency/alpha level of the text overlay, cast to a byte value (0-255 range). If not provided, defaults to 0."
            },
            {
              "name": "tileCoords[0]",
              "description": "The X and Y position for the text overlay. If not provided, both X and Y default to 0."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "textOverlay_dynamic",
      "uses": [
        {
          "description": "Displays an overlay text message with dynamic character-by-character reveal animation. The text appears gradually, revealing one character at a time, creating a typewriter effect. Additional flags control dismissibility and auto-reveal behavior.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The text message to display with dynamic reveal."
            },
            {
              "name": "floats[0]",
              "description": "The transparency/alpha level of the text overlay, cast to a byte value (0-255 range)."
            },
            {
              "name": "bools[0]",
              "description": "When true, the text cannot be dismissed by the player (inverted internally). When false, the player can dismiss the text."
            },
            {
              "name": "bools[1]",
              "description": "When true, the text does not auto-reveal character by character (inverted internally). When false, the text reveals gradually."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The X and Y position for the text overlay. If not provided, both X and Y default to 0."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "pointCameraAt",
      "uses": [
        {
          "description": "Points the camera at a specific tile coordinate with a fixed camera mode. The camera focuses on the specified location, discovers fog of war at that position, and optionally adjusts tracking speed. Flags can lock the camera for the duration of a preventInteraction delay and enable special roof rendering.",
          "required": [
            {
              "name": "bools[0]",
              "description": "When true, locks the camera for the duration of the current preventInteraction delay timer. When false, the camera can be changed immediately."
            },
            {
              "name": "bools[1]",
              "description": "When true, fixes the camera to render as if under a roof. This affects visual rendering of the scene."
            }
          ],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The camera tracking speed. If non-zero, overrides the default tracking speed. If zero or not provided, uses default speed."
            },
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the camera should point. Must be provided (not (-1, -1)) to use this mode."
            }
          ]
        },
        {
          "description": "Points the camera at a specific actor, tracking their movement. The camera follows the actor as they move around the zone. This is triggered when no tile coordinate is provided and an actor ID is specified.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to track with the camera. The actor must exist in the current zone."
            },
            {
              "name": "bools[0]",
              "description": "When true, locks the camera for the duration of the current preventInteraction delay timer. When false, the camera can be changed immediately."
            },
            {
              "name": "bools[1]",
              "description": "When true, fixes the camera to render as if under a roof. This affects visual rendering of the scene."
            }
          ],
          "optional": []
        },
        {
          "description": "Points the camera at the task's assigned actor or the current actor, tracking their movement. This is triggered when no tile coordinate or actor ID string is provided. The camera follows the actor as they move.",
          "required": [
            {
              "name": "bools[0]",
              "description": "When true, locks the camera for the duration of the current preventInteraction delay timer. When false, the camera can be changed immediately."
            },
            {
              "name": "bools[1]",
              "description": "When true, fixes the camera to render as if under a roof. This affects visual rendering of the scene."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "lockCameraAt",
      "uses": [
        {
          "description": "Locks the camera at a specific tile coordinate with a fixed camera mode. The camera is permanently locked at the specified location until explicitly unlocked, discovers fog of war at that position, and optionally adjusts tracking speed. A roof rendering flag can be enabled.",
          "required": [
            {
              "name": "bools[0]",
              "description": "When true, fixes the camera to render as if under a roof. This affects visual rendering of the scene."
            }
          ],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The camera tracking speed. If non-zero, overrides the default tracking speed. If zero or not provided, uses default speed."
            },
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the camera should be locked. Must be provided (not (-1, -1)) to use this mode."
            }
          ]
        },
        {
          "description": "Locks the camera on a specific actor, permanently tracking their movement. The camera is locked to follow the actor until explicitly unlocked. This is triggered when no tile coordinate is provided.",
          "required": [
            {
              "name": "bools[0]",
              "description": "When true, fixes the camera to render as if under a roof. This affects visual rendering of the scene."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "scaleCameraTo",
      "uses": [
        {
          "description": "Scales (zooms) the camera to a target zoom level. If a tile coordinate is provided, the scaling happens gradually over time at a specified speed. Otherwise, the camera instantly snaps to the target scale.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The target camera scale (zoom level). Values greater than 1.0 zoom in, less than 1.0 zoom out."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "If provided and X > 0, the X coordinate (divided by 10) determines the scaling speed for a gradual zoom. If not provided or X <= 0, the camera snaps instantly to the target scale."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setRoofDrawNoMatterWhat",
      "uses": [
        {
          "description": "Controls whether roofs are always drawn regardless of camera position or other factors. When enabled, roofs render even when they normally wouldn't be visible, which can be useful for specific visual effects or debugging.",
          "required": [
            {
              "name": "bools[0]",
              "description": "When true, forces roofs to always draw. When false, roofs use normal rendering logic."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cancelIfEnemiesPresent",
      "uses": [
        {
          "description": "Cancels task execution if hostile enemies are present within a specified distance from a location. The check can optionally filter for specific enemy types. If the distance is 0, any hostile enemy in the zone cancels the task. The location defaults to the task actor's position or can be specified with a tile coordinate.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The maximum distance in tiles. Enemies closer than this distance will cancel the task. If 0, any hostile enemy in the entire zone cancels the task regardless of distance."
            }
          ],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The actor type ID to filter for. If provided, only enemies of this specific type will be checked. If empty or not provided, all hostile enemies are checked."
            },
            {
              "name": "tileCoords[0]",
              "description": "The center point for the distance check. If not provided (or (-1, -1)) and a task actor exists, uses the task actor's position. Otherwise uses the provided tile coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "cancelIfEnemiesNotPresent",
      "uses": [
        {
          "description": "Cancels task execution if no hostile enemies are present within a specified distance from a location. This is the inverse of cancelIfEnemiesPresent. If the distance is 0, at least one hostile enemy anywhere in the zone is required to continue. The location defaults to the task actor's position or can be specified with a tile coordinate.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The maximum distance in tiles. If no enemies are found closer than this distance, the task is canceled. If 0, at least one hostile enemy must exist in the entire zone to continue."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The center point for the distance check. If not provided (or (-1, -1)) and a task actor exists, uses the task actor's position. Otherwise uses the provided tile coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "cancelIfItemPresent",
      "uses": [
        {
          "description": "Cancels task execution if a specific item is present on a tile or within an area. When a tile coordinate is provided, checks that single tile. When no tile coordinate is provided, checks all tiles in the task's area bounds. If the item is found on any checked tile, the task is canceled.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to check for. If this item is found on any of the checked tiles, the task is canceled."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The specific tile to check. If not provided (or (-1, -1)), checks all tiles within the task's area bounds instead."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "cancelIfItemNotPresent",
      "uses": [
        {
          "description": "Cancels task execution if a specific item is not present on a tile or within an area. When a tile coordinate is provided, that tile must have the item or the task is canceled. When no tile coordinate is provided, at least one tile in the task's area bounds must have the item to continue.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to check for. The item must be present for the task to continue."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The specific tile to check. If not provided (or (-1, -1)), checks all tiles within the task's area bounds, requiring at least one to have the item."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "cancelIf",
      "uses": [
        {
          "description": "Cancels task execution based on a formula evaluation. The formula is calculated and if the result is greater than 0 (or greater than or equal to 1 when a boolean flag is set), the task continues. Otherwise, the task is canceled. This provides flexible conditional logic for task execution.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The formula string to evaluate. The formula can reference actor values, global variables, and other game state."
            },
            {
              "name": "floats[0]",
              "description": "A parameter passed to the formula calculation that can be used within the formula."
            },
            {
              "name": "bools[0]",
              "description": "Changes the cancellation condition. When false, cancels if result <= 0. When true, cancels if result < 1 (allowing the task to continue only when result >= 1)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "facingRot",
      "uses": [
        {
          "description": "Sets the facing rotation of an actor. The actor will face the specified direction (rotation in degrees). This affects which direction the actor appears to be looking or facing, but does not move the actor.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The rotation angle in degrees. Determines which direction the actor faces."
            }
          ],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose rotation should be set. If provided and non-empty, that actor's rotation is set. If empty or not provided, the task's assigned actor's rotation is set."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "facingRotParty",
      "uses": [
        {
          "description": "Sets the facing rotation of all party members simultaneously. All party members will face the same specified direction. This is useful for coordinating party orientation during cutscenes or scripted events.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The rotation angle in degrees. All party members will face this direction."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "song",
      "uses": [
        {
          "description": "Plays a background music song with customizable volume, pitch, fade-in delay, and override settings. Special keywords 'travelSong'/'travel' and 'combatSong'/'combat' play the zone's configured songs. The song loops continuously until changed or stopped.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The song ID to play. Use 'travelSong'/'travel' for zone's travel song, 'combatSong'/'combat' for zone's combat song, or any valid song ID. If empty or not provided, stops current music."
            },
            {
              "name": "tileCoords[0]",
              "description": "X coordinate as volume percentage (0-100), Y coordinate as fade-in delay in seconds. If X is -1, volume defaults to 100%. If Y is -1, delay defaults to 0."
            },
            {
              "name": "floats[0]",
              "description": "The pitch adjustment for the song. 0 is normal pitch, positive values increase pitch, negative values decrease pitch."
            },
            {
              "name": "bools[0]",
              "description": "When true, this song overrides travel-based song changes, keeping it playing even when traveling between zones."
            },
            {
              "name": "bools[1]",
              "description": "When true, sets fade-in delay to -99, creating an instant start with no fade-in."
            }
          ]
        }
      ],
      "aliases": ["playSong"]
    },
    {
      "name": "playNearestLocationSong",
      "uses": [
        {
          "description": "Plays the external song associated with the nearest location to the player's current position. This automatically selects music based on proximity to locations, creating dynamic music that reflects the player's geographic context.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setZoneSongs",
      "uses": [
        {
          "description": "Sets the zone's travel and combat songs along with their audio properties. The appropriate song (travel or combat) immediately begins playing based on the current game mode. Song properties include volume, pitch, and combat song delay.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The song ID for travel (non-combat) mode. Can be empty if bools[0] is true."
            },
            {
              "name": "strings[1]",
              "description": "The song ID for combat mode. Can be empty if bools[1] is true."
            },
            {
              "name": "floats[0]",
              "description": "The pitch adjustment applied to both travel and combat songs."
            },
            {
              "name": "bools[0]",
              "description": "When true, allows setting travel song even if strings[0] is empty, effectively silencing travel music."
            },
            {
              "name": "bools[1]",
              "description": "When true, allows setting combat song even if strings[1] is empty, effectively silencing combat music."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "X coordinate as volume percentage (0-100) for both songs. If X is -1, volume defaults to 100%. Y coordinate is the combat song delay in seconds."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "removeItem",
      "uses": [
        {
          "description": "Removes items from a tile coordinate or travel point. The item ID can be provided directly or retrieved from a global variable. When a travel point flag is set, interprets the tile coordinate as a travel point identifier instead of a tile position.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to remove from the tile or travel point."
            },
            {
              "name": "floats[0]",
              "description": "The number of items to remove. If 0, automatically becomes 1. Only used in inventory removal mode."
            },
            {
              "name": "bools[1]",
              "description": "When true, interprets tileCoords[0] as travel point coordinates instead of a tile coordinate."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The name of a global variable containing the item ID. If provided and non-empty, retrieves the item ID from this global variable instead of using strings[0]."
            },
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate (or travel point coordinates if bools[1] is true) where items should be removed. Must be provided (not (-1, -1)) to use tile/point removal mode."
            }
          ]
        },
        {
          "description": "Removes items from the task actor's inventory, falling back to party inventories, stash, and held items if not found. Attempts to remove the specified quantity, searching through available inventories in order until the items are found and removed.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to remove from inventories."
            },
            {
              "name": "floats[0]",
              "description": "The number of items to remove. If 0, automatically becomes 1."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The name of a global variable containing the item ID. If provided and non-empty, retrieves the item ID from this global variable instead of using strings[0]."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "removeActorItem",
      "uses": [
        {
          "description": "Removes and unequips a specific item from a specific actor's inventory. If the actor has the item equipped, it is first unequipped before removal. If the actor or item doesn't exist, this task does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose inventory should be modified."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the item to remove from the actor's inventory."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeItemOfValue",
      "uses": [
        {
          "description": "Removes items of a specific value or higher from a tile coordinate. This removes items based on their monetary value rather than their ID, useful for removing valuable items regardless of type.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The minimum value threshold. Items with this value or higher will be removed from the tile."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate from which to remove valuable items."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "removeEmptyItem",
      "uses": [
        {
          "description": "Removes all instances of a specific container item from the entire zone if they are empty. This searches every tile in the zone and removes containers of the specified type that have no items inside them, useful for cleaning up empty storage containers.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the container item type to remove when empty. Only containers with this ID that have 0 items inside will be removed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeItemsNearPlayerActor",
      "uses": [
        {
          "description": "Removes items within a specified radius of the player's current position. The item ID can be provided directly or retrieved from a global variable. This searches all tiles within range and removes matching items from each tile.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to remove from tiles near the player."
            },
            {
              "name": "floats[0]",
              "description": "The maximum distance in tiles from the player. Items on tiles within this radius will be removed."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The name of a global variable containing the item ID. If provided and non-empty, retrieves the item ID from this global variable instead of using strings[0]."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "removeAllItems",
      "uses": [
        {
          "description": "Removes all items from a specific tile coordinate. All items on that tile are removed at once.",
          "required": [],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate from which all items should be removed. Must be provided and not (-1, -1)."
            }
          ]
        },
        {
          "description": "Removes all instances of a specific item from every tile in the entire zone. The item ID can be provided directly or retrieved from a global variable. This performs a zone-wide cleanup of the specified item type.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to remove from all tiles in the zone."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The name of a global variable containing the item ID. If provided and non-empty, retrieves the item ID from this global variable instead of using strings[0]."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "removeAllItemsOfValue",
      "uses": [
        {
          "description": "Removes all items meeting a minimum value threshold from every tile in the entire zone. This searches the entire zone and removes any items with value greater than or equal to the specified threshold, useful for clearing valuable loot across the map.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The minimum value threshold. Items with this value or higher will be removed from all tiles in the zone."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeHeldItems",
      "uses": [
        {
          "description": "Removes all items from the held items inventory (items currently being carried or transferred). This clears the temporary held items storage, typically used for items being moved or traded.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addItem",
      "uses": [
        {
          "description": "Adds an item to a specific tile coordinate. The item is placed on the ground at the specified location with an optional force-fit flag.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to add. Must be a valid item type defined in the game data."
            },
            {
              "name": "bools[0]",
              "description": "Force-fit flag. When true, forces the item to be placed even if the tile is full."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of items to add. If less than 1, it uses 1 instead."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the item should be placed. Must be provided (not (-1, -1))."
            }
          ]
        },
        {
          "description": "Adds an item to a specific actor's inventory. The actor automatically equips or stores the item. If the actor's inventory is full, the item is dropped at their position.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to add. Must be a valid item type defined in the game data."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the actor who should receive the item. The actor must exist in the current zone."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of items to add. If less than 1, it uses 1 instead."
            }
          ],
          "optional": []
        },
        {
          "description": "Adds an item to the task actor's inventory, cargo, or stash depending on item type. Cargo items go to cargo inventory, other items are equipped on the actor if possible. If the actor is controllable, plays a pickup sound. Only adds items the actor doesn't already have.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to add. Must be a valid item type defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of items to add. If less than 1, it uses 1 instead."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addItemToContainer",
      "uses": [
        {
          "description": "Adds an item into a container (like a chest or bag) located at a specific tile. Searches for a suitable container on the tile that can fit the item and adds it inside. Can optionally specify which container to target by container ID.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to add to the container. Must be a valid item type defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of items to add. If less than 1, it uses 1 instead."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The ID of the specific container item to target. If provided and non-empty, only containers with this ID will be used. If empty or not provided, any suitable container is used."
            },
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the container is located. The task searches this tile for suitable containers."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "spawnItem",
      "uses": [
        {
          "description": "Spawns an item at a tile coordinate or travel point. Can optionally spawn a visual effect at the spawn location. The item ID can be provided directly or retrieved from a global variable. Travel point interpretation can be enabled with a flag.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to spawn, or the name of a global variable containing the item ID. Must be a valid item type (either directly or after global variable lookup)."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of items to add. If less than 1, it uses 1 instead."
            },
            {
              "name": "bools[0]",
              "description": "Force-fit flag. When true, forces the item to spawn even if the tile is full."
            },
            {
              "name": "bools[1]",
              "description": "When true, interprets tileCoords[0] as travel point coordinates instead of a tile coordinate."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The ID of a visual effect to play at the spawn location. If provided and non-empty, displays this effect when the item spawns."
            },
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate (or travel point coordinates if bools[1] is true) where the item should spawn. Must be provided (not (-1, -1))."
            }
          ]
        },
        {
          "description": "Spawns an item at a random location within the task's area bounds. Attempts to find a suitable location where the item can fit, trying up to 50 times before giving up. Can optionally spawn a visual effect at the spawn location.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item to spawn, or the name of a global variable containing the item ID. Must be a valid item type (either directly or after global variable lookup)."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of items to add. If less than 1, it uses 1 instead."
            },
            {
              "name": "bools[0]",
              "description": "Force-fit flag. When true, forces the item to spawn even if the chosen tile is full."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The ID of a visual effect to play at the spawn location. If provided and non-empty, displays this effect when the item spawns."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "moveItems",
      "uses": [
        {
          "description": "Moves all items from the task's top-left area coordinate to a destination tile. All items are removed from the source tile and added to the destination, with force-fit enabled to ensure they are placed even if the destination is full.",
          "required": [],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The destination tile coordinate where items should be moved to."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "moveItemsToActor",
      "uses": [
        {
          "description": "Moves all items from a tile to a specific actor's inventory. The actor attempts to equip each item, dropping any that don't fit on the ground. After all items are transferred, the source tile is cleared of all items.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor who should receive the items. The actor must exist in the current zone."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate from which items should be taken and moved to the actor."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "replaceEquipment",
      "uses": [
        {
          "description": "Replaces an equipped item on an actor with a new item. The actor's current item in the specified slot is unequipped and the new item is equipped in that slot. The slot number is automatically adjusted based on item category (armor uses slot 2 if slot 0 is specified, accessories use slot 3 if slot 0 is specified).",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose equipment should be replaced. The actor must exist in the current zone."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the new item to equip. Must be a valid item type defined in the game data."
            },
            {
              "name": "floats[0]",
              "description": "The equipment slot number. For weapons, uses this slot directly. For armor, slot 0 becomes slot 2. For accessories, slot 0 becomes slot 3."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "dropItem",
      "uses": [
        {
          "description": "Removes an item from an actor's inventory and drops it on the ground at the actor's position. The specified quantity is removed from the actor's inventory and placed on the tile where they are standing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor who should drop the item. The actor must exist in the current zone."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the item to drop from the actor's inventory."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of items to drop. If 0, automatically becomes 1."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buryAllItemsOfType",
      "uses": [
        {
          "description": "Buries or unburies all items of a specific type or with a specific special property across the entire zone. Buried items become invisible and non-interactable. This searches every tile in the zone and modifies matching items. Can match by item ID or by special property.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item type to bury/unbury. All items with this ID will be affected."
            },
            {
              "name": "bools[0]",
              "description": "When false, buries the items (makes them invisible). When true, unburies the items (makes them visible)."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The special property name to match. If provided and non-empty, items with this special property will also be buried/unburied, in addition to items matching strings[0]."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "buryGlobalVarItems",
      "uses": [
        {
          "description": "Automatically buries or unburies special items across the zone based on global variable values. Items with the 'setGlobalVarWhenConverted' property are unburied if their corresponding global variable is greater than 0, otherwise they are buried. This creates dynamic item visibility based on game state.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buryLocations",
      "uses": [
        {
          "description": "Buries all location marker items for undiscovered locations in the current zone. Location items at and adjacent to undiscovered location coordinates are made invisible, hiding them until the location is discovered. This searches for items with the 'location' special property.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "reactOnMatchingItems",
      "uses": [
        {
          "description": "Triggers elemental reactions on pairs of matching items that face each other across the zone. Searches the entire zone for items of a specific type, checks if they face a matching item (or a second specified type) in their facing direction, and triggers 'matched' or 'matchedSelf' reactions on both items when a match is found.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the primary item type to search for. Items of this type will react when they face matching items."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The ID of the secondary item type to match with. If provided and non-empty, primary items react to this type. If empty or not provided, primary items react to other items of the same type (strings[0])."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "specialKeyStatusUpdate",
      "uses": [
        {
          "description": "Triggers a special reaction on all party members when they are near key pedestals. Searches the entire zone for 'pedestal_keyhole' items and causes all party members to react with the 'nearKeyPedestal' element, typically used for quest or puzzle state updates.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeActor",
      "uses": [
        {
          "description": "Removes an actor from the zone by their ID. Optionally plays a visual effect when removing the actor and can transfer the actor's inventory to the player's stash before removal.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to remove from the zone."
            },
            {
              "name": "bools[0]",
              "description": "Whether to play a visual effect when removing the actor. When true, plays an FX at the actor's position."
            },
            {
              "name": "bools[1]",
              "description": "Whether to transfer the actor's inventory to the player's stash before removing them. When true, all items the actor is carrying are moved to the stash."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The ID of the visual effect to play when removing the actor. Only used if bools[0] is true. Defaults to 'actor_flee' if not provided."
            }
          ]
        },
        {
          "description": "Removes an actor at a specific tile coordinate from the zone. Optionally plays a visual effect when removing the actor and can transfer the actor's inventory to the player's stash before removal.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to play a visual effect when removing the actor. When true, plays an FX at the actor's position."
            },
            {
              "name": "bools[1]",
              "description": "Whether to transfer the actor's inventory to the player's stash before removing them. When true, all items the actor is carrying are moved to the stash."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The ID of the visual effect to play when removing the actor. Only used if bools[0] is true. Defaults to 'actor_flee' if not provided."
            },
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate where the actor to remove is located. Must not be (-1, -1)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "removeActorType",
      "uses": [
        {
          "description": "Removes all actors of a specific type from the zone. Iterates through all actors in the current zone and removes those matching the specified actor type ID. Optionally plays a visual effect at each removed actor's position.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor type ID to match. All actors with this actorTypeID will be removed from the zone."
            },
            {
              "name": "bools[0]",
              "description": "Whether to play a visual effect when removing each actor. When true, plays the 'actor_flee' FX at each removed actor's position."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeActors_despawnable",
      "uses": [
        {
          "description": "Removes despawnable actors from the zone, the special zone if one exists, and clears any held despawnable actor. Despawnable actors are typically temporary or enemy actors that can be cleaned up. When limited to incapacitated actors, this awards XP for defeated hostile enemies that give XP when in combat mode. Also removes despawnable actors from the special zone and clears the held actor if they are despawnable.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to play a visual effect when removing each actor. When true, plays the 'actor_flee' FX at each removed actor's position."
            },
            {
              "name": "bools[1]",
              "description": "When true, only removes incapacitated or drowning despawnable actors. When false, removes all despawnable actors regardless of their state. This affects both the main zone and actor selection for XP awarding."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorFloat",
      "uses": [
        {
          "description": "Sets a numeric property on a specific actor to a float value. Uses the DataManager to assign the value by converting it to a string, allowing modification of any numeric actor property that can be set through the data system.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose property will be modified."
            },
            {
              "name": "strings[1]",
              "description": "The name of the property to set on the actor. This should be a valid numeric property name that exists on the actor."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to assign to the property."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorBool",
      "uses": [
        {
          "description": "Sets a boolean property on a specific actor. The value is determined by a logical OR of a float and a boolean parameter - the property becomes true if either the float is greater than zero or the boolean is true. This allows flexible boolean assignment from either numeric or boolean inputs.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose property will be modified."
            },
            {
              "name": "strings[1]",
              "description": "The name of the boolean property to set on the actor. This should be a valid boolean property name that exists on the actor."
            },
            {
              "name": "floats[0]",
              "description": "A numeric value used in the boolean determination. If greater than zero, the property is set to true."
            },
            {
              "name": "bools[0]",
              "description": "A boolean value used in the boolean determination. If true, the property is set to true."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorAI",
      "uses": [
        {
          "description": "Changes an actor's AI script to a new script. The actor's Lua AI system will switch to executing the specified script. Optionally runs the script immediately once after switching to it, which can be useful for initialization or triggering immediate AI behavior changes.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose AI script will be changed."
            },
            {
              "name": "strings[1]",
              "description": "The name of the AI script to switch to. This should be a valid Lua script name that the AI system can execute."
            },
            {
              "name": "bools[0]",
              "description": "Whether to run the AI script once immediately after switching to it. When true, executes the script specified in strings[0] (note: this appears to use the actor ID, not the script name)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorName",
      "uses": [
        {
          "description": "Changes the display name of a specific actor. This modifies how the actor appears in dialogs, UI elements, and other places where the actor's name is shown.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose name will be changed."
            },
            {
              "name": "strings[1]",
              "description": "The new name to assign to the actor."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorIdleAnimOverride",
      "uses": [
        {
          "description": "Overrides an actor's idle animation with a specific animation. When set, the actor will play the specified animation instead of their default idle animation when not performing other actions.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose idle animation will be overridden."
            },
            {
              "name": "strings[1]",
              "description": "The name of the animation to use as the idle animation override."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorDialogNodeID",
      "uses": [
        {
          "description": "Changes the dialog node that an actor uses when interacted with. This determines what dialog conversation branch the actor will start when the player talks to them.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose dialog node will be changed."
            },
            {
              "name": "strings[1]",
              "description": "The dialog node ID to assign to the actor. This should be a valid node ID from the game's dialog system."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorAIActions",
      "uses": [
        {
          "description": "Sets the queued AI actions for a specific actor. This replaces the actor's current AI action queue with a new list of actions, allowing precise control over what the actor will do next. The action list is specified as a string and parsed by the data system.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose AI actions will be set."
            },
            {
              "name": "strings[1]",
              "description": "A string representation of the queued AI actions list. This should follow the format expected by the DataManager for the queuedAiActions property, including empty entries if needed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setActorPalettes",
      "uses": [
        {
          "description": "Changes all three color palettes of an actor (armor, skin, and cloth) to the same palette simultaneously. This is useful for creating uniform color schemes or visual effects that affect the actor's entire appearance.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose palettes will be changed."
            },
            {
              "name": "strings[1]",
              "description": "The palette name to apply to all three palette types (armorPalette, skinPalette, and clothPalette)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setFaceSprite",
      "uses": [
        {
          "description": "Changes the face sprite of the task's assigned actor. Modifies the visual appearance of the actor's face by setting a new sprite for the 'face' body part.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The sprite name to use for the actor's face. This should be a valid face sprite identifier."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setCurrentActor_partSpritesThatTakePriorityOverClass",
      "uses": [
        {
          "description": "Sets which body part sprites take priority over the current actor's class defaults. This allows specific visual customizations to override the standard appearance defined by the actor's class. The current actor is the one actively being controlled or selected.",
          "required": [
            {
              "name": "strings[0]",
              "description": "A string representation of the body part sprites list that should take priority over class defaults. This should follow the format expected by the DataManager for the partSpritesThatTakePriorityOverClass property."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "giveActorsBaseXP",
      "uses": [
        {
          "description": "Awards base experience points to all actors in the current zone. Each actor receives their standard base XP reward, typically used for rewarding participation in events or encounters.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveActor",
      "uses": [
        {
          "description": "Moves a specific actor to a tile coordinate and sets their facing rotation. This teleports the actor to the exact position specified rather than pathfinding or walking.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to move."
            },
            {
              "name": "floats[0]",
              "description": "The facing rotation to set for the actor after moving them, in degrees."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate to move the actor to."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "moveActor_point",
      "uses": [
        {
          "description": "Moves a specific actor to the nearest travel point matching the specified coordinates and sets their facing rotation. This finds a travel point item in the zone and moves the actor to that location.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to move."
            },
            {
              "name": "floats[0]",
              "description": "The facing rotation to set for the actor after moving them to the travel point, in degrees."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The coordinates used to identify which travel point to find. The X and Y values are used to construct the travel point identifier."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "movePartyTo",
      "uses": [
        {
          "description": "Moves all party members to a specific tile coordinate and points the camera at that location. Optionally sets the facing rotation for all party members. Also adds a brief interaction delay if in travel mode to prevent accidental interactions after teleporting.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The facing rotation to set for all party members after moving them, in degrees. If 0, party members retain their current facing."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate to move the party to. The camera will also target this position."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "movePartyTo_point",
      "uses": [
        {
          "description": "Moves all party members to the nearest travel point matching the specified coordinates. Optionally repositions party members onto specific travel points and spawns player boats in boat zones. This is commonly used for zone transitions and positioning the party at predefined locations.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to put party actors onto travel points after moving. When true, repositions party members onto travel point items matching the Y coordinate from tileCoords[0]."
            },
            {
              "name": "bools[1]",
              "description": "Whether to spawn player boats in boat zones. When true and the current zone is a boat zone, adds all boats from the player fleet to the zone and party if they aren't already present."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The coordinates used to identify which travel point to move the party to. The X and Y values are used to construct the travel point identifier."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "movePartyBy",
      "uses": [
        {
          "description": "Moves all party members by a relative offset vector and shifts the camera by the same amount. Optionally sets the facing rotation for all party members. The offset is added to each party member's current position, with bounds checking to keep actors within the zone.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The facing rotation to set for all party members after moving them, in degrees. If 0, party members retain their current facing."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The X and Y offset to move each party member by, relative to their current positions."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "moveAllActorsBy",
      "uses": [
        {
          "description": "Moves all actors in the zone by a relative offset vector and shifts the camera by the same amount. Optionally sets the facing rotation for all actors. The offset is added to each actor's current position, with bounds checking to keep actors within the zone. This affects all actors, not just party members.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The facing rotation to set for all actors after moving them, in degrees. If 0, actors retain their current facing."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The X and Y offset to move each actor by, relative to their current positions."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "addToParty",
      "uses": [
        {
          "description": "Adds a specific actor to the player's party. If the actor is not found in the zone, the task fails and if used in a trigger, cancels the rest of the trigger's effects.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to add to the party. The actor must exist in the current zone."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addTalkerToParty",
      "uses": [
        {
          "description": "Adds the task's assigned actor (typically the actor the player is talking to) to the party as a converted ally and fleet member. If the actor's ID starts with 'hire_', renames them to a unique 'hired_' ID to mark them as hired crew. Slightly reduces crew morale if it's above 70. This is designed for recruiting NPCs during dialogs.",
          "required": [],
          "optional": [
            {
              "name": "bools[0]",
              "description": "Whether to try adding the actor to the landing party (active party members). When true, attempts to add them to the active party rather than just the barracks or fleet."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "addTalkerToParty_temp",
      "uses": [
        {
          "description": "Adds the task's assigned actor to the party as a temporary ally with restricted permissions. The actor cannot have their appearance edited, corpse looted, or be fully controlled. Sets their cloth palette to match the main player. Refreshes their action setup and may add difficulty mode support abilities if applicable. This is designed for temporary companion characters in quests or missions.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeFromParty",
      "uses": [
        {
          "description": "Removes a specific actor from the player's party without adding them to the barracks. Optionally also removes them from the fleet. If the actor is not currently in the party, the task fails and if used in a trigger, cancels the rest of the trigger's effects.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to remove from the party. The actor must currently be in the party."
            }
          ],
          "optional": [
            {
              "name": "bools[0]",
              "description": "Whether to also remove the actor from the fleet. When true, removes them from the fleet in addition to the party."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "handleTiredStates",
      "uses": [
        {
          "description": "Manages rest and recovery for party members. Active party members are healed without restoring max HP and receive a 'tired' debuff of -2. Barracks crew members are fully healed, with max HP restoration always applied regardless of the 'valueThisRun' global variable check (the condition includes 'or true'). This is typically used at the end of a day or after resting.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "hideParty",
      "uses": [
        {
          "description": "Makes all party members invisible for a specified duration. Sets an invisibility timer on each party member, during which they will not be rendered or visible in the game world.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The duration in seconds for which party members will be invisible."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "path",
      "uses": [
        {
          "description": "Sets a pathfinding route for a specific actor to follow. The actor will either pathfind directly to the destination or follow a waypoint-based route. Optionally modifies the actor's movement speed. The actor's AI target is set to themselves during pathing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to path. If the actor is not found in the zone, falls back to the task's assigned actor."
            },
            {
              "name": "bools[0]",
              "description": "Whether to use waypoint-based pathfinding. When true, finds a path using waypoints and reverses it. When false, uses standard A* pathfinding with the actor's movement area of effect and terrain considerations."
            },
            {
              "name": "floats[0]",
              "description": "The movement speed to set for the actor during pathing. If greater than 0, overrides the actor's default movement speed. If 0 or less, the actor uses their default speed."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The destination tile coordinate for the actor to path to."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "pathParty",
      "uses": [
        {
          "description": "Sets pathfinding routes for all party members to the same destination. Each party member pathfinds individually from their current position to the target coordinate using standard A* pathfinding. Party members' AI targets are set to themselves during pathing.",
          "required": [],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The destination tile coordinate for all party members to path to."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "destination",
      "uses": [
        {
          "description": "Sets a destination for a specific actor without creating a path. The actor's AI will pathfind to this destination on its own. Clears any existing path and AI target. Optionally modifies the actor's movement speed.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to set a destination for. If the actor is not found in the zone, falls back to the task's assigned actor."
            },
            {
              "name": "floats[0]",
              "description": "The movement speed to set for the actor. If greater than 0, overrides the actor's default movement speed. If 0 or less, the actor uses their default speed."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The destination tile coordinate for the actor to move toward."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "movement",
      "uses": [
        {
          "description": "Applies direct movement input to an actor, causing them to move in a specific direction. Clears existing paths and movement inputs, makes the actor intangible, and optionally plays a pain animation and modifies movement speed. The movement vector is calculated from the actor's current position to the target coordinate, with -1 values preserving the current axis position.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to apply movement to. If the actor is not found or the string is empty, falls back to the task's assigned actor."
            },
            {
              "name": "bools[0]",
              "description": "Whether to play a pain animation on the actor during movement. When true, animates the actor with their pain animation at normal speed."
            },
            {
              "name": "floats[0]",
              "description": "The movement speed to set for the actor. If greater than 0, overrides the actor's default movement speed. If 0 or less, the actor uses their default speed."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The target tile coordinate. For each axis, -1 preserves the actor's current position on that axis; otherwise, the actor moves toward the specified coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "addZoneFX",
      "uses": [
        {
          "description": "Adds a visual effect to the zone at a specific world position. Optionally prevents adding duplicate effects if the same FX already exists in the zone. The effect persists in the zone and can be positioned at specific coordinates.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to add to the zone. This should be a valid FX identifier."
            },
            {
              "name": "bools[0]",
              "description": "Whether to check for duplicates before adding. When true, only adds the effect if it doesn't already exist in the zone. When false, adds the effect regardless of existing instances."
            },
            {
              "name": "floats[0]",
              "description": "Duration or timing parameter for the zone effect. The exact meaning depends on the effect type."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The world position coordinates (X, Y) where the visual effect should be placed in the zone."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "createZoneSpecialFX",
      "uses": [
        {
          "description": "Generates and adds tiled special effects covering the entire zone. Creates a grid of 1024x1024 pixel texture tiles, each with generated special FX textures based on the zone terrain. These are typically used for water effects or atmospheric overlays. Can optionally save the generated textures to desktop and supports custom velocity offsets and depth modifications.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The generation mode or parameter for creating the special FX textures. Affects how the textures are generated from the zone terrain."
            },
            {
              "name": "bools[0]",
              "description": "Whether to create underwater/background effects. When true, effects render behind everything with a large negative depth and use water FX alpha modulation. When false, effects render in front with a large positive depth."
            },
            {
              "name": "floats[0]",
              "description": "Alpha offset for the effects. When non-zero, sets a custom alpha offset value (as negative integer) for the FX tiles, affecting their transparency."
            }
          ],
          "optional": [
            {
              "name": "bools[1]",
              "description": "Whether to save the generated textures to the desktop. When true, saves each generated tile texture as an image file for debugging or inspection."
            },
            {
              "name": "tileCoords[0]",
              "description": "Velocity offset for the FX tiles. When not (-1, -1), sets a velocity offset of (X * 0.1, Y * 0.1) for each tile, enables looping around zone edges, and makes effects only draw when roofs are drawn."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "removeZoneFX",
      "uses": [
        {
          "description": "Removes a visual effect from the zone by its ID. Can remove either the first instance or all instances of the effect.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the visual effect to remove from the zone."
            },
            {
              "name": "bools[0]",
              "description": "Whether to remove all instances of this effect. When true, removes every instance of this FX ID from the zone. When false, removes only the first instance found."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setZonePalettes",
      "uses": [
        {
          "description": "Changes the color palette for zone terrain types. Can target specific terrain palettes (stone, dirt, grass, water, wood, and misc1-8 variations) or set all palettes at once by using an unrecognized palette type name. Accepts various case variations and naming formats for each palette type.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The palette type to modify. Can be: 'stone', 'stoneDark', 'stoneLight', 'dirt', 'dirtDark', 'grass', 'grassDark', 'water', 'misc1' through 'misc8', 'wood', or 'woodDark' (case-insensitive, with or without 'zone' prefix/suffix). Any other value sets all terrain palettes to the same palette."
            },
            {
              "name": "strings[1]",
              "description": "The palette name to apply to the specified terrain type(s)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setZonePeaceful",
      "uses": [
        {
          "description": "Toggles the zone's peaceful state, which affects whether combat can occur and actor invincibility. Exits combat mode if currently active. Refreshes peaceful invincibility state on all actors when the peaceful state changes.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to make the zone peaceful. When true, sets the zone to peaceful mode (no combat). When false, allows combat and clears the cantAct restriction."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "disableFog",
      "uses": [
        {
          "description": "Permanently disables the fog of war for the current zone. Both the zone's fog tracking and the camera's fog rendering are turned off, revealing the entire zone.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setZoneRollingPinStyle",
      "uses": [
        {
          "description": "Changes the zone's rolling pin rendering style. This affects how the zone is rendered, likely related to scrolling or tiling effects.",
          "required": [
            {
              "name": "bools[0]",
              "description": "The rolling pin style to apply. True and false likely enable different rendering modes for the zone."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "screenDarkWaveEffect",
      "uses": [
        {
          "description": "Applies a wave effect to the screen darkness shader. Changes the wave sine frequency and triggers the shader timing modulation. Only updates if the new frequency differs from the current value.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The wave sine frequency to apply to the screen dark shader. Affects the visual distortion pattern of the darkness overlay."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setZoneDarkness",
      "uses": [
        {
          "description": "Enables zone darkness and sets the darkness color. Optionally also applies the darkness color to the camera immediately. This affects the dark overlay color used when the zone is marked as dark.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The color name to use for the zone darkness. Should be a valid color identifier that can be looked up in the game's color data. Alternatively, this can be a color-component value with \"R,G,B\" or \"R,G,B,A\" with values specified between 0-255. Note: When specified as a task string, the color components will be split up and probably crash the game. It's possible to set a global variable with setGlobalVarWithCommas and then use @GmyColorVar to correctly insert a color-component value into a task string."
            },
            {
              "name": "bools[0]",
              "description": "Whether to also apply the darkness color to the camera immediately. When true, skips the gradual transition to the new color."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["changeZoneDarkColor", "setZoneDarkColor"]
    },
    {
      "name": "setZoneDarknessToWorldDarkness",
      "uses": [
        {
          "description": "Enables zone darkness and sets the darkness color to match the current outdoor world darkness based on time of day and location. Uses the current location to determine the appropriate darkness color, applying town zone color modulation if the location is a port. Optionally applies the darkness to the camera immediately.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to also apply the darkness color to the camera immediately. When true, sets the camera's darkness color to match the calculated world darkness color."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "screenWaveEffect",
      "uses": [
        {
          "description": "Applies a wave effect to the screen shader. Changes the wave sine frequency and triggers the shader timing modulation. Only updates if the new frequency differs from the current value.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The wave sine frequency to apply to the screen shader. Affects the visual distortion pattern of the wave effect."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setZoneBG",
      "uses": [
        {
          "description": "Changes the zone's background terrain type. This determines what terrain texture is used for the zone background rendering.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The background terrain type ID to use for the zone."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setZoneBGFX",
      "uses": [
        {
          "description": "Changes the zone's background visual effect. This determines what background FX is rendered behind the zone.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The background FX ID to use for the zone."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "initWaypoints",
      "uses": [
        {
          "description": "Initializes or refreshes the waypoint navigation system for the current zone. Forces a complete refresh of waypoint data used for AI pathfinding.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "safe",
      "uses": [
        {
          "description": "Marks the zone manager as safe. This likely prevents certain dangerous events or encounters from occurring.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "sendAISignal",
      "uses": [
        {
          "description": "Sends a signal to the Lua AI system at the task's assigned actor's position. The signal can carry an integer parameter and is broadcast to AI scripts that listen for it.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The signal name to send to the AI system. AI scripts can listen for and respond to specific signal names."
            },
            {
              "name": "floats[0]",
              "description": "An integer parameter to send with the signal. Converted to int, this can carry additional data about the signal."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "disableTrigger",
      "uses": [
        {
          "description": "Prevents a trigger from executing by marking it as done in the current zone. Can identify the trigger by either its string ID or numeric ID. Once disabled, the trigger will not execute until re-enabled.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The string ID of the trigger to disable. If non-empty and a matching trigger exists, this ID is used to look up the trigger's numeric ID. If empty, uses floats[0] instead."
            },
            {
              "name": "floats[0]",
              "description": "The numeric ID of the trigger to disable. Used directly if strings[0] is empty, or overridden by the numeric ID from the string lookup if strings[0] is non-empty and valid."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enableTrigger",
      "uses": [
        {
          "description": "Re-enables a previously disabled trigger, allowing it to execute again. Can identify the trigger by either its string ID or numeric ID. Removes the trigger from the flagged-as-done list for the current zone.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The string ID of the trigger to enable. If non-empty and a matching trigger exists, this ID is used to look up the trigger's numeric ID. If empty, uses floats[0] instead."
            },
            {
              "name": "floats[0]",
              "description": "The numeric ID of the trigger to enable. Used directly if strings[0] is empty, or overridden by the numeric ID from the string lookup if strings[0] is non-empty and valid."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["resetTrigger"]
    },
    {
      "name": "trigger",
      "uses": [
        {
          "description": "Executes a trigger from the zone or global trigger list. Identifies triggers by string ID. Optionally evaluates a formula condition before executing and can check trigger requirements before execution.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The string ID of the trigger to execute. Searches zone triggers first, then global triggers."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "A formula string to evaluate as a condition. If provided and non-empty, the trigger only executes if the formula evaluates to a value greater than 0."
            },
            {
              "name": "bools[0]",
              "description": "When true, skips the trigger's requirement checks and executes it unconditionally (except for the optional formula condition in strings[1]). When false or when bools is empty, checks the trigger's normal requirements before executing."
            },
            {
              "name": "bools[1]",
              "description": "Parameter passed to the trigger's checkIfCanTrigger method when bools[0] is false. The meaning depends on the trigger implementation. Only used when checking trigger requirements."
            }
          ]
        },
        {
          "description": "Executes a trigger from the zone. Identifies triggers by numeric ID. Optionally evaluates a formula condition before executing and can check trigger requirements before execution. The trigger is looked up first by string ID from zone or global triggers, then by numeric ID if no string ID is provided.",
          "required": [
            {
              "name": "strings[0]",
              "description": "When empty, uses floats[0] to look up by numeric ID instead."
            },
            {
              "name": "floats[0]",
              "description": "The numeric ID of the trigger to execute from the zone's trigger list. Only used if strings[0] is empty."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "A formula string to evaluate as a condition. If provided and non-empty, the trigger only executes if the formula evaluates to a value greater than 0."
            },
            {
              "name": "bools[0]",
              "description": "When true, skips the trigger's requirement checks and executes it unconditionally (except for the optional formula condition in strings[1]). When false or when bools is empty, checks the trigger's normal requirements before executing."
            },
            {
              "name": "bools[1]",
              "description": "Parameter passed to the trigger's checkIfCanTrigger method when bools[0] is false. The meaning depends on the trigger implementation. Only used when checking trigger requirements."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "addTrigger",
      "uses": [
        {
          "description": "Adds a global trigger to the current zone if it doesn't already exist in the zone. The trigger is cloned from the global trigger definitions and assigned a new numeric ID.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the global trigger to add to the zone. Must exist in the global trigger definitions."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeTrigger",
      "uses": [
        {
          "description": "Removes a trigger from the current zone by its string ID. The trigger is permanently removed from the zone's trigger list.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The string ID of the trigger to remove from the zone. Only removes the trigger if this is provided and a matching trigger exists."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "specialSpawn",
      "uses": [
        {
          "description": "Applies a special spawn pattern to the zone. Special spawns are predefined spawn configurations that place specific actors or items in designated locations.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the special spawn pattern to apply to the zone."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn",
      "uses": [
        {
          "description": "Spawns an actor in the zone from an actor prefab. If no tile coordinate is provided, places the actor at a random occupiable tile within the trigger area. If a tile coordinate is provided, the actor is placed at exactly that position regardless of whether the tile is occupiable. If the actor prefab ID does not exist, it falls through to the spawn data version of this task (see second use), which has different argument meanings.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor prefab ID. If this doesn't exist, it falls through to the spawn data version (see second use), which has different argument meanings."
            },
            {
              "name": "strings[1]",
              "description": "The animation to play on spawn."
            },
            {
              "name": "bools[0]",
              "description": "Whether to preserve the exact actor ID instead of generating a unique one."
            },
            {
              "name": "floats[0]",
              "description": "The actor's facing rotation in degrees."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The tile coordinate to spawn the actor at. If not provided, a random occupiable tile in the trigger area is used. If provided, the actor is placed at exactly this position regardless of occupiability."
            }
          ]
        },
        {
          "description": "Spawns actors in the zone from spawn data. Creates a group of actors at a power level relative to the party. If no tile coordinate is provided, actors are placed at random occupiable tiles within the trigger area. If a tile coordinate is provided, the spawn area starts at that tile and extends toward the bottom-right: Y is enlarged by 1 and X is enlarged by 1.5 times the number of actors to spawn. If only one actor is being spawned and the exact tile is occupiable, it spawns at exactly that tile. If no occupiable tiles are found in the spawn area, nothing is spawned.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The spawn data ID. Note: It will first check for an actor prefab with this ID, so make sure the IDs aren't shared."
            },
            {
              "name": "strings[1]",
              "description": "Group ID override for the created actors."
            },
            {
              "name": "bools[0]",
              "description": "Whether spawned actors should look at the center of the spawn area instead of the player."
            },
            {
              "name": "bools[1]",
              "description": "Whether spawned actors should face the player. Only takes effect if bools[0] is false. If this is false, but an X value was specified, actors will also look at the player."
            },
            {
              "name": "floats[0]",
              "description": "Power level offset added to party power level, enemy spawn modifier, and spawnPowerMod global variable."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The spawn area origin. If not provided, actors spawn at random occupiable tiles within the trigger area. If provided, the spawn area extends from this tile toward the bottom-right (Y+1, X + 1.5 * actor count), and actors are placed at random occupiable tiles within that area."
            }
          ]
        }
      ],
      "aliases": ["spawn_actorPrefab"]
    },
    {
      "name": "spawn_actorPrefab_point",
      "uses": [
        {
          "description": "Spawns an actor in the zone from an actor prefab at a travel point specified by (X, Y) with optional ID preservation and animation.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor prefab ID. If this doesn't exist, it falls through to the spawn data version (see second use), which has different argument meanings."
            },
            {
              "name": "bools[0]",
              "description": "Whether to preserve the exact actor ID instead of generating a unique one."
            },
            {
              "name": "floats[0]",
              "description": "The actor's facing rotation in degrees."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The animation to play on spawn."
            },
            {
              "name": "tileCoords[0]",
              "description": "Travel point coordinates. The X value selects which travel point within the set (by index), and the Y value identifies which travel point set to use (travel point type)."
            }
          ]
        },
        {
          "description": "Spawns actors in the zone from spawn data. Creates a group of actors at a power level relative to the party. Travel points are not used. If no tile coordinate is provided, actors are placed at random occupiable tiles within the trigger area. If a tile coordinate is provided, the spawn area starts at that tile and extends toward the bottom-right: Y is enlarged by 1 and X is enlarged by 1.5 times the number of actors to spawn. If only one actor is being spawned and the exact tile is occupiable, it spawns at exactly that tile. If no occupiable tiles are found in the spawn area, nothing is spawned.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The spawn data ID. Note: It will first check for an actor prefab with this ID, so make sure the IDs aren't shared."
            },
            {
              "name": "bools[0]",
              "description": "Whether spawned actors should look at the center of the spawn area instead of the player."
            },
            {
              "name": "bools[1]",
              "description": "Whether spawned actors should face the player. Only takes effect if bools[0] is false. If this is false, but an X value was specified, actors will also look at the player."
            },
            {
              "name": "floats[0]",
              "description": "Power level offset added to party power level, enemy spawn modifier, and spawnPowerMod global variable."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "Group ID override for the created actors."
            },
            {
              "name": "tileCoords[0]",
              "description": "The spawn area origin. If not provided, actors spawn at random occupiable tiles within the trigger area. If provided, the spawn area extends from this tile toward the bottom-right (Y+1, X + 1.5 * actor count), and actors are placed at random occupiable tiles within that area."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn_g",
      "uses": [
        {
          "description": "Spawns actors from spawn data whose ID is stored in a global variable. Retrieves the spawn data ID from a global string variable and creates a spawn task with that ID. Useful for dynamically determining which actors to spawn based on game state.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global string variable containing the spawn data ID to use."
            },
            {
              "name": "strings[1]",
              "description": "Group ID override for the created actors."
            },
            {
              "name": "bools[0]",
              "description": "Whether spawned actors should look at the center of the spawn area instead of the player."
            },
            {
              "name": "bools[1]",
              "description": "When true and coordinates are provided, or when true regardless of coordinates, spawned actors look at the player instead of facing randomly."
            },
            {
              "name": "floats[0]",
              "description": "Power level offset added to party power level, enemy spawn modifier, and spawnPowerMod global variable."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The spawn position or search area. Behavior matches the spawn task."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn_g_nearPlayer",
      "uses": [
        {
          "description": "Spawns actors from spawn data whose ID is stored in a global variable, positioning them near the player. Retrieves the spawn data ID from a global string variable and creates actors in a small area around the player's position. Optionally spawns behind the player instead of at their position.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global string variable containing the spawn data ID to use."
            },
            {
              "name": "strings[1]",
              "description": "Group ID override for the created actors."
            },
            {
              "name": "bools[0]",
              "description": "Whether spawned actors should look at the center of the spawn area instead of the player."
            },
            {
              "name": "bools[1]",
              "description": "Whether to spawn behind the player. When true, spawns 3 tiles behind the player in the opposite direction they're facing. When false, spawns at the player's position."
            },
            {
              "name": "floats[0]",
              "description": "Power level offset added to party power level, enemy spawn modifier, and spawnPowerMod global variable."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "Additional positioning parameters passed to the spawn task."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn_actorPrefab_point_specialZone",
      "uses": [
        {
          "description": "Spawns an actor from a prefab into the special zone at a specific travel point. Creates the actor at a travel point within a set, using coordinates to identify which travel point set and index. The actor is fully healed, has all effects removed, and is added to the special zone rather than the main zone.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor prefab ID to spawn. Must be a valid prefab in the actor prefab definitions."
            },
            {
              "name": "bools[0]",
              "description": "Whether to preserve the exact actor ID instead of generating a unique one."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "Animation to play on the spawned actor."
            },
            {
              "name": "tileCoords[0]",
              "description": "Travel point coordinates. The X value selects which travel point within the set (by index), and the Y value identifies which travel point set to use (travel point type)."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn_location",
      "uses": [
        {
          "description": "Spawns actors appropriate for the current location based on the location's auto-spawn data. Creates 1-3 spawn groups (more for wealthier locations) from the location's spawn list and positions them on travel points. Also triggers special port spawns if the player owns a home. This is typically used to populate towns and settlements with NPCs.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn_onItem",
      "uses": [
        {
          "description": "Spawns actors from spawn data on tiles containing items with a specific special property. Creates actors at power level relative to the party, positions them on matching item tiles, and optionally makes them look at a target position. The actors are marked as giving XP and positioned randomly on matching items.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The spawn data ID to use for creating actors."
            },
            {
              "name": "strings[1]",
              "description": "The special property name to search for on items. Actors spawn on tiles containing items with this property."
            },
            {
              "name": "bools[0]",
              "description": "Whether to prevent group duplication. When true, does not spawn if a group with this ID already exists."
            },
            {
              "name": "floats[0]",
              "description": "Power level offset added to party power level, enemy spawn modifier, and spawnPowerMod global variable."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "Optional target position. When not (-1, -1), spawned actors look at this coordinate after spawning."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn_seamonster",
      "uses": [
        {
          "description": "Spawns a sea monster fleet near the task's assigned actor if they are adjacent to deep water. Searches for a monster beacon item to determine which sea monster type to spawn, or uses a default if none found. Creates a hostile sea monster fleet with cargo and positions it in deep water near the actor. Plays a spawn visual effect.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The power level for the sea monster spawn."
            }
          ],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The sea monster spawn data ID to use. If empty or not provided, searches for a monster beacon item within 50 tiles to determine the spawn type (sm0-sm9), defaulting to sm0 if no beacon is found."
            }
          ]
        }
      ],
      "aliases": ["spawn_sm"]
    },
    {
      "name": "spawn_fleet",
      "uses": [
        {
          "description": "Spawns a complete fleet with ships and crew at a specific position. Creates a fleet using spawn data configurations, with options for merchant status, allied status, and custom faction.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The spawn data ID to use for creating the fleet. Must exist in spawn data definitions."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the actor prefab to use as commodore of the fleet."
            },
            {
              "name": "bools[0]",
              "description": "Whether the fleet is a merchant fleet."
            },
            {
              "name": "bools[1]",
              "description": "Whether the fleet is elite."
            },
            {
              "name": "floats[0]",
              "description": "Desired power level of the fleet, clamped to the minimum and maximum actors defined in the fleet spawn data."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The position where the fleet should spawn."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "spawn_corpse",
      "uses": [
        {
          "description": "Spawns a random corpse actor from the party's death list into the zone. Selects a corpse that hasn't been spawned yet (tracks up to 2 spawned corpses via global variables). Positions the corpse at the specified coordinate or in the middle of the bounding area if no coordinate is provided.",
          "required": [],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The position to spawn the corpse. When (-1, -1), spawns in the middle of the task's bounding area."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "talk",
      "uses": [
        {
          "description": "Initiates dialog with a specific actor or displays dialog without an actor. Can optionally position the camera between the player and talker, and can adjust the dialog UI vertical position. If no valid actor ID is provided, displays dialog without pointing the camera.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor to talk to. If empty or the actor doesn't exist in the zone, displays dialog without an associated actor."
            },
            {
              "name": "bools[0]",
              "description": "When false, points the camera between the player and the actor being talked to. When true, skips camera positioning."
            },
            {
              "name": "bools[1]",
              "description": "When true, overrides the Y position to place the dialog UI at the bottom of the screen (screen height - 10 pixels)."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "Dialog node ID or additional parameter for the dialog system."
            },
            {
              "name": "tileCoords[0]",
              "description": "The Y component is used for the dialog UI vertical position (unless overridden by bools[1])."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "tryDialog",
      "uses": [
        {
          "description": "Attempts to start a dialog by its ID. If successful and a tile coordinate is provided, points and fixes the camera at that coordinate during the dialog.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog ID to attempt to start."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "Camera target position during dialog. When not (-1, -1) and dialog successfully starts, fixes the camera at this position."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "tryDialog_commodore",
      "uses": [
        {
          "description": "Attempts to start a dialog by its ID, and if successful, points the camera at the enemy fleet's flagship and plays an exclamation visual effect above it. Designed for encounters with enemy fleet commanders.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog ID to attempt to start."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "exitDialog",
      "uses": [
        {
          "description": "Exits dialog mode if currently in a dialog. Changes the game mode from dialog to travel mode.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "attractAttention",
      "uses": [
        {
          "description": "Attracts the attention of nearby actors to a specific location. Makes hostile actors aware and potentially aggressive. Can operate at the task's assigned actor's position or at a specified coordinate, with customizable radius and actor selection criteria.",
          "required": [],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The position to attract attention to. When (-1, -1), uses the task's assigned actor's position instead."
            },
            {
              "name": "bools[0]",
              "description": "If true, affects actors regardless of other conditions: being able to act, having intelligence > 0, matching roof state (normally only attracts if both the actor and the target position are under a roof, or neither is), and having an AI that allows changing behavior (e.g. not patrol or idle_ignoreAttention). Note: only has effect when no tileCoords[0] is provided."
            },
            {
              "name": "floats[0]",
              "description": "The radius distance for the attention effect. Defaults to 12 if not provided or if 0."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "attractAttentionToPlayer",
      "uses": [
        {
          "description": "Attracts the attention of nearby actors to the player's position. Makes hostile actors aware and potentially aggressive toward the player. Uses customizable radius and actor selection criteria.",
          "required": [],
          "optional": [
            {
              "name": "bools[0]",
              "description": "If true, affects actors regardless of other conditions: being able to act, having intelligence > 0, matching roof state (normally only attracts if both the actor and the target position are under a roof, or neither is), and having an AI that allows changing behavior (e.g. not patrol or idle_ignoreAttention)."
            },
            {
              "name": "floats[0]",
              "description": "The radius distance for the attention effect. Defaults to 12 if not provided or if 0."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "executeDialogEffect",
      "uses": [
        {
          "description": "Executes a special dialog effect by name. Dialog effects are predefined actions that can be triggered from dialog nodes.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the special dialog effect to execute."
            },
            {
              "name": "floats[0]",
              "description": "Numeric parameter passed to the dialog effect."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "Additional string parameter passed to the dialog effect."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "loadSpecialZone",
      "uses": [
        {
          "description": "Loads and positions a special zone overlay at a specific world position. The special zone is displayed over the main zone and can be scaled, positioned with precision, and configured to draw only when roofs are visible. Supports optional depth modification for rendering order.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the special zone to load."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The scale factor for the special zone. When provided and parseable as a float, sets both the current and target scale."
            },
            {
              "name": "bools[0]",
              "description": "Whether the special zone should only draw when roofs are being drawn. Useful for interior overlays."
            },
            {
              "name": "floats[0]",
              "description": "Depth modification for the special zone rendering. Affects the draw order relative to other elements."
            },
            {
              "name": "tileCoords[0]",
              "description": "The world position for the special zone. The X and Y values set the position directly."
            }
          ]
        }
      ],
      "aliases": ["loadSpecialZone_precision"]
    },
    {
      "name": "loadSpecialZone_playerFlagship",
      "uses": [
        {
          "description": "Loads the player's flagship boarding zone as a special zone overlay at a specific world position. Automatically determines the boarding zone ID from the player fleet's flagship. The special zone can be scaled, positioned with precision, and configured to draw only when roofs are visible.",
          "required": [],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The scale factor for the special zone. When provided and parseable as a float, sets both the current and target scale."
            },
            {
              "name": "bools[0]",
              "description": "Whether the special zone should only draw when roofs are being drawn. Useful for interior overlays."
            },
            {
              "name": "floats[0]",
              "description": "Depth modification for the special zone rendering. Affects the draw order relative to other elements."
            },
            {
              "name": "tileCoords[0]",
              "description": "The world position for the special zone. The X and Y values set the position directly."
            }
          ]
        }
      ],
      "aliases": ["loadSpecialZone_playerFlagship_precision"]
    },
    {
      "name": "moveSpecialZone",
      "uses": [
        {
          "description": "Moves the special zone to a new target position with optional movement speed and easing. Can optionally create a yo-yo effect where the special zone returns to its starting position after reaching the target.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to use easing for the movement. When true, the movement is smoothed/eased rather than linear."
            },
            {
              "name": "floats[0]",
              "description": "The movement speed. If greater than 0, overrides the default special zone movement speed."
            }
          ],
          "optional": [
            {
              "name": "bools[1]",
              "description": "Whether to create a yo-yo effect. When true, the special zone will return to its current position after reaching the target."
            },
            {
              "name": "tileCoords[0]",
              "description": "The target position to move the special zone to. The X and Y values set the target directly."
            }
          ]
        }
      ],
      "aliases": ["moveSpecialZone_precision"]
    },
    {
      "name": "scaleSpecialZone",
      "uses": [
        {
          "description": "Scales the special zone to a target scale factor with optional scaling speed. The scale value determines how large the special zone appears relative to its original size.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The target scale factor as a parseable float string. Sets how large the special zone should be scaled to."
            },
            {
              "name": "floats[0]",
              "description": "The scaling speed. If greater than 0, overrides the default special zone scaling speed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "mergeSpecialZone",
      "uses": [
        {
          "description": "Merges the special zone into the main zone, making its contents permanent. Destroys previous items in the merge area and integrates the special zone's actors, items, and terrain into the main zone. Optionally repositions the special zone before merging.",
          "required": [],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "Position to move the special zone to before merging. When not (-1, -1), repositions the special zone to this location before the merge."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "barracksLineup",
      "uses": [
        {
          "description": "Arranges all party members and barracks crew in a grid formation for display. Positions actors in rows with customizable spacing, with active party members slightly elevated. All actors face 90 degrees (east) and are added to the zone if not already present.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The spacing between actors in the grid. Defaults to 1 if 0 or less. Determines horizontal distance between adjacent actors."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The starting position for the lineup grid. Actors are arranged in rows starting from this coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "unlockExtras",
      "uses": [
        {
          "description": "Unlocks all extra game content including bonus races, options, and achievements. Makes all player-unlockable actor types available for character creation, enables extra options, and triggers achievement for winning on any difficulty. Saves the updated configuration to the config file.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "trySetAchievement",
      "uses": [
        {
          "description": "Attempts to set a Steam achievement and records it in the game's global variables. Sets the achievement both in the game state and on Steam if the Steam API is initialized and running.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The Steam achievement ID to set. This should match a valid achievement identifier in the game's Steam configuration."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "retro",
      "uses": [
        {
          "description": "Toggles retro visual mode on or off. Switches between modern and retro graphics styles by flipping the RetroVisualsMode flag.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resetLocations",
      "uses": [
        {
          "description": "Resets the current location's state. This refreshes location data and may respawn NPCs, items, or reset quest states for the current location.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "refreshUndamagedShipZones",
      "uses": [
        {
          "description": "Resets all boarding zones for undamaged player ships. Deletes saved zone data for each boat's boarding zone, effectively resetting their interior layouts and contents. This is useful when ship conditions have changed.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "boardActor",
      "uses": [
        {
          "description": "Enters the boarding zone of a specific actor (typically a ship). Loads the actor's boarding zone and transitions the player into it. Adds a brief interaction delay to prevent accidental inputs.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor whose boarding zone to enter. This should be a ship or other boardable actor."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "Additional parameter for the boarding zone transition."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "endBoarding",
      "uses": [
        {
          "description": "Exits the current boarding zone and returns to the main world zone. Optionally specifies which zone to return to.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The zone ID to return to. If not provided, returns to the default previous zone."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "enterFleetBattle",
      "uses": [
        {
          "description": "Initiates a fleet battle between two ships or fleets. Can specify combatants explicitly or automatically find a nearby enemy for the task's assigned actor. Searches for hostile actors within 2 tiles if combatants aren't specified.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The ID of the first combatant actor (typically the flagship of one fleet)."
            },
            {
              "name": "strings[1]",
              "description": "The ID of the second combatant actor (typically the flagship of the opposing fleet)."
            }
          ]
        }
      ],
      "aliases": ["enterFleetBattle_player"]
    },
    {
      "name": "setDialogSpeed",
      "uses": [
        {
          "description": "Changes the dialog text display speed multiplier. Values greater than 1 speed up dialog text, while values less than 1 slow it down. Affects how quickly dialog text appears character-by-character.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The dialog speed multiplier. Higher values make text appear faster, lower values make it appear slower."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setMoney",
      "uses": [
        {
          "description": "Sets the player's money to a specific amount. Can set directly from a numeric value or calculate from a formula string.",
          "required": [
            {
              "name": "strings[0]",
              "description": "A formula string to calculate the money amount. If empty, uses floats[0] instead. If non-empty, evaluates the formula to determine the amount."
            },
            {
              "name": "floats[0]",
              "description": "The money amount to set directly. Only used if strings[0] is empty."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterCombat",
      "uses": [
        {
          "description": "Initiates combat mode at a specific position with a customizable battlefield radius. Optionally specifies which actor initiated the combat. The position determines the center of the combat area.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The ID of the actor initiating the combat. Used for tracking combat initiation and potentially affecting combat setup."
            },
            {
              "name": "floats[0]",
              "description": "The battlefield radius in tiles. Defaults to 18 if not provided or if 0. Determines how large the combat area is."
            },
            {
              "name": "tileCoords[0]",
              "description": "The center position of the combat battlefield. Combat encounters are centered around this coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "enterCombat_timer",
      "uses": [
        {
          "description": "Initiates combat mode immediately and creates a timed action that executes after a delay. The action timer is added to the zone's action timers and will execute the specified action after the timer expires.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The action ID to execute when the timer expires."
            },
            {
              "name": "floats[0]",
              "description": "The delay in seconds before the timed action executes."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The position for both the combat center and the timed action's target coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "exitCombat",
      "uses": [
        {
          "description": "Exits combat mode if currently in combat. Forces combat to end regardless of victory or defeat status. Optionally determines whether to award victory.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to treat the combat exit as victorious. When false, exits as victorious. When true, exits as not victorious (inverted logic)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "offerRest",
      "uses": [
        {
          "description": "Prompts the player with a rest confirmation dialog if in travel mode. Checks if it's safe to rest first and shows an appropriate message. If safe, offers rest; if not safe, warns the player.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "advanceTimers",
      "uses": [
        {
          "description": "Advances the zone's action timers to the next timer event. Processes timers in the zone by advancing time by a specified amount, triggering timers that expire within that timeframe.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The time amount to advance timers by. This value is used for both minimum and maximum advance parameters."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "scoreScreen",
      "uses": [
        {
          "description": "Displays an end-of-mission score screen showing money earned, special loot collected, surviving party members, and XP earned. Converts all money and convertible items to score, processes special items that set global variables, awards XP equally among survivors, and updates mission success tracking. The score determines the 'valueThisRun' metric used for progression.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setupGraveyard",
      "uses": [
        {
          "description": "Arranges all dead party members in a graveyard formation. Places deceased actors on platforms in a grid pattern, makes them petrified and uncontrollable, heals them, and drops their inventories. Actors are positioned in rows with 2-tile spacing, up to a maximum of 122 corpses.",
          "required": [],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The starting position for the graveyard grid. Dead actors are arranged in rows starting from this coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "endGame",
      "uses": [
        {
          "description": "Returns to the main menu and handles end-game cleanup. If extras were unlocked this session, triggers the unlock notification after a 1-second delay. Resets the unlock tracking flag.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "handleDeaths",
      "uses": [
        {
          "description": "Processes actors who are dying and converts them to dead status. For controllable (party) actors, creates timed death events with visual effects. For non-controllable actors, immediately marks them as dead with visual effects. Only processes actors who have the 'dying' status effect that has expired.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "getResponse_combat",
      "uses": [
        {
          "description": "Generates an NPC negotiation response for a combat intent. Calculates the enemy's response based on relative power levels and sets the dialog's next node to the response. Uses the last float parameter as an additional modifier for the negotiation calculation.",
          "required": [
            {
              "name": "floats[0]",
              "description": "Modifier value used in the negotiation calculation. This is accessed as the last element in the floats array."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["getResponse_peace", "getResponse_bribe"]
    },
    {
      "name": "getBribeAmount",
      "uses": [
        {
          "description": "Displays the bribe amount required to appease an enemy in the current dialog. Calculates the amount based on the enemy combat group's power level (14% of power level) and updates the dialog statement to show the cost.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtractBribeAmount",
      "uses": [
        {
          "description": "Subtracts the calculated bribe amount from the player's money and adds it to the task's assigned actor's inventory. The amount is based on the actor's combat group power level (14% of power level).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "add50gp",
      "uses": [
        {
          "description": "Adds 50 gold pieces to the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addgp",
      "uses": [
        {
          "description": "Adds money to the player. Can add a fixed amount or calculate from a formula string.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "A formula string to calculate the money amount to add. If provided, evaluates this formula to determine the amount."
            },
            {
              "name": "floats[0]",
              "description": "A fixed money amount to add. Only used if strings[0] is not provided."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "subtractgp",
      "uses": [
        {
          "description": "Subtracts money from the player. Can subtract a fixed amount or calculate from a formula string.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "A formula string to calculate the money amount to subtract. If provided, evaluates this formula to determine the amount."
            },
            {
              "name": "floats[0]",
              "description": "A fixed money amount to subtract. Only used if strings[0] is not provided."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract10gp",
      "uses": [
        {
          "description": "Subtracts 10 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract20gp",
      "uses": [
        {
          "description": "Subtracts 20 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract30gp",
      "uses": [
        {
          "description": "Subtracts 30 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract40gp",
      "uses": [
        {
          "description": "Subtracts 40 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract50gp",
      "uses": [
        {
          "description": "Subtracts 50 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract60gp",
      "uses": [
        {
          "description": "Subtracts 60 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract75gp",
      "uses": [
        {
          "description": "Subtracts 75 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract100gp",
      "uses": [
        {
          "description": "Subtracts 100 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract130gp",
      "uses": [
        {
          "description": "Subtracts 130 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract150gp",
      "uses": [
        {
          "description": "Subtracts 150 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract200gp",
      "uses": [
        {
          "description": "Subtracts 200 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract250gp",
      "uses": [
        {
          "description": "Subtracts 250 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract300gp",
      "uses": [
        {
          "description": "Subtracts 300 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract350gp",
      "uses": [
        {
          "description": "Subtracts 350 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract400gp",
      "uses": [
        {
          "description": "Subtracts 400 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract500gp",
      "uses": [
        {
          "description": "Subtracts 500 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract800gp",
      "uses": [
        {
          "description": "Subtracts 800 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract1000gp",
      "uses": [
        {
          "description": "Subtracts 1000 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract5000gp",
      "uses": [
        {
          "description": "Subtracts 5000 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtract10000gp",
      "uses": [
        {
          "description": "Subtracts 10000 gold pieces from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "subtractAllgp",
      "uses": [
        {
          "description": "Sets the player's money to zero, removing all gold pieces.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "giveGlobalVarGP",
      "uses": [
        {
          "description": "Adds money to the player based on the value of a global variable. Converts the global variable's float value to an integer money amount.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable whose value will be added as money."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterSellMode",
      "uses": [
        {
          "description": "Opens party member inventories in sell/trade mode. Displays all party inventories in a standard layout centered vertically on screen, enabling item selling or trading.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "openCargo",
      "uses": [
        {
          "description": "Opens the cargo inventory UI, allowing the player to view and manage the fleet's cargo hold.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "autobuy_food",
      "uses": [
        {
          "description": "Automatically purchases as much food as possible with available money and cargo space. Buys food items up to either the cargo capacity limit or the maximum affordable with current money, whichever is lower. Adds the food to the cargo inventory.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterSellMode_cargo",
      "uses": [
        {
          "description": "Opens the cargo inventory in sell/trade mode. Displays the fleet's cargo hold, enabling selling or trading of cargo items.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterSellMode_stash",
      "uses": [
        {
          "description": "Opens the stash inventory in sell/trade mode. Displays the player's stash storage, enabling selling or trading of stashed items.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterBuyMode",
      "uses": [
        {
          "description": "Opens buy mode with an NPC's inventory for purchase. If the NPC has items in containers (such as barrels or chests), it displays the first container for browsing; otherwise, it displays the NPC's full inventory. The player's stash is also opened alongside for receiving purchased items.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterBuyMode_location",
      "uses": [
        {
          "description": "Opens buy mode for purchasing goods from the current location's cargo inventory. The location's cargo (trade goods available for sale) is displayed as the buying source, and the player's fleet cargo is opened for receiving the purchased goods. This task only works when the player is currently at a location.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterBuyMode_locationItems",
      "uses": [
        {
          "description": "Opens buy mode for purchasing items from the current location's stash inventory. The location's stash (items available for sale) is displayed as the buying source, and the player's personal stash is opened for receiving the purchased items. This task only works when the player is currently at a location.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterBuyMode_fleet",
      "uses": [
        {
          "description": "Opens buy mode for purchasing goods from an NPC fleet's cargo. The cargo of the fleet associated with the task's assigned actor is displayed as the buying source, and the player's fleet cargo is opened for receiving the purchased goods.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setBuyPriceMult",
      "uses": [
        {
          "description": "Sets the price multiplier for buy transactions. This affects how much the player pays when purchasing items or goods. A value of 1.0 is the standard price, values less than 1.0 provide a discount, and values greater than 1.0 increase the cost.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The price multiplier to apply to buy transactions. Standard pricing is 1.0."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buyItem_confirmWindow",
      "uses": [
        {
          "description": "Opens a confirmation window asking the player how many of a specific item to buy. The window includes a slider for selecting quantity. If the item is a cargo good, the cargo UI is opened; otherwise, the stash UI is opened. If the quantity is 0, it defaults to 1.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item type to purchase."
            },
            {
              "name": "floats[0]",
              "description": "The initial quantity to display in the slider. If set to 0, it will default to 1."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buyGoods_confirmWindow",
      "uses": [
        {
          "description": "Opens a confirmation window asking the player how many trade goods to buy from the current location. The window includes a slider for selecting quantity. The trade good type is automatically determined from the current location. If the quantity is 0, it defaults to 1.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The initial quantity to display in the slider. If set to 0, it will default to 1."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buyFood_fillCargo",
      "uses": [
        {
          "description": "Automatically purchases as much food as possible to fill the player's cargo hold. Calculates the remaining cargo capacity and buys food to fill it, limited by the player's available money. The food is added to the fleet's cargo inventory and the cost is deducted from the player's money.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buyFood",
      "uses": [
        {
          "description": "Purchases a specified quantity of food. Calculates the cost based on the food's value and the requested quantity. If the player has enough money, the cost is deducted and the food is added to the fleet's cargo inventory.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The quantity of food to purchase."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buyGoods",
      "uses": [
        {
          "description": "Purchases a specified quantity of the current location's trade good. The trade good type is automatically determined from the location associated with the current zone. The cost is calculated based on the good's value and requested quantity. If the player has enough money, the cost is deducted and the goods are added to the fleet's cargo inventory.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The quantity of trade goods to purchase."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "giveFood",
      "uses": [
        {
          "description": "Gives the player a specified quantity of food at no cost. The food is added directly to the fleet's cargo inventory without any money transaction.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The quantity of food to give to the player."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "lose10Food",
      "uses": [
        {
          "description": "Removes exactly 10 units of food from the fleet's cargo inventory.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeCargo",
      "uses": [
        {
          "description": "Removes a specified quantity of a specific item type from the fleet's cargo inventory.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item type to remove from cargo."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of the item to remove."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeCargo_g",
      "uses": [
        {
          "description": "Removes a specified quantity of an item type from the fleet's cargo inventory, where the item ID is retrieved from a global variable. The global variable should contain the item type ID as a string value.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable containing the item type ID to remove from cargo."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of the item to remove."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeItem_g",
      "uses": [
        {
          "description": "Removes a specified quantity of an item type from any party member's inventory, where the item ID is retrieved from a global variable. The global variable should contain the item type ID as a string value. The item is searched for across all party members and removed when found.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable containing the item type ID to remove from the party."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of the item to remove."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeItemFromParty",
      "uses": [
        {
          "description": "Removes a specified quantity of a specific item type from any party member's inventory. The item is searched for across all party members and removed when found.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the item type to remove from the party."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of the item to remove."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "refreshStatMods",
      "uses": [
        {
          "description": "Refreshes stat modifiers for all actors in the current zone and all party members (both active party and barracks). This recalculates any temporary or equipment-based stat bonuses and penalties, ensuring all character statistics are up to date.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "grantXPShare_5percent",
      "uses": [
        {
          "description": "Grants shared experience points to all party members (both active party and barracks) if the XPShare global variable is set. Awards 5% of the accumulated experience in each class's global XP pool to each party member. This only applies to classes that have accumulated more than 100 XP in their pool. The awarded XP is not affected by party size and ignores tired states.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "hostile",
      "uses": [
        {
          "description": "Marks the task's assigned actor as hostile, making them aggressive towards the player.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "playerInitiateCombat",
      "uses": [
        {
          "description": "Makes the player's actor initiate combat with the task's assigned actor. Both actors are animated to idle first, then the player attacks the target actor. This represents the player starting the fight.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enemyInitiateCombat",
      "uses": [
        {
          "description": "Makes the task's assigned actor initiate combat with the player. Both actors are animated to idle first, then the target actor attacks the player. This represents the enemy starting the fight.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "giveQuestItemReward",
      "uses": [
        {
          "description": "Rewards the player with money based on the value of a quest item. The quest item ID is retrieved from the 'tavern_questItem' global variable, and the player receives money equal to that item's value. The money amount is also added to the 'earnedCashTotal' global variable for tracking.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "reassignQuestItem",
      "uses": [
        {
          "description": "Generates a new random quest item. Creates a 'loot_quest' item and generates its actual contents, then stores the resulting item ID in the 'tavern_questItem' global variable. This is used to assign new quest objectives.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "gotoNodeIf",
      "uses": [
        {
          "description": "Conditionally jumps to a different dialog node if a formula evaluates to true (value >= 1). The formula is evaluated in the context of the task's assigned actor. If the condition is met, the dialog immediately switches to the specified node.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID to jump to if the condition is met."
            },
            {
              "name": "strings[1]",
              "description": "A formula expression that determines whether to jump. If it evaluates to 1 or greater, the jump occurs."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["gotoIf"]
    },
    {
      "name": "gotoNodeDelay",
      "uses": [
        {
          "description": "Jumps to a different dialog node after a specified delay. During the delay, player interaction is prevented. After the delay completes, the dialog resumes at the specified node.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID to jump to after the delay."
            },
            {
              "name": "floats[0]",
              "description": "The delay duration in seconds before jumping to the new node."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "delayDialog",
      "uses": [
        {
          "description": "Pauses the dialog for a specified duration. The dialog window is hidden during the delay, and player interaction is prevented. After the delay completes, the dialog automatically resumes where it left off.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The delay duration in seconds."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "incOnNewDay",
      "uses": [
        {
          "description": "Increments the actor's dialog node ID if at least 24 in-game hours have passed since the last increment. Tracks the last increment time using a global variable named 'incOnNewDay_' followed by the current node ID. If enough time has passed, the node ID is incremented (e.g., 'node1' becomes 'node2') and the dialog jumps to the new node. Always updates the tracking variable with the current world time.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "An optional delay parameter passed to the dialog node change function. Purpose unclear from the code."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "incDefaultNode",
      "uses": [
        {
          "description": "Increments the task's assigned actor's default dialog node ID. If the actor has a dialog node set, it's incremented to the next sequential ID (e.g., 'node1' becomes 'node2'). If the node is empty, no action is taken.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setMyNode",
      "uses": [
        {
          "description": "Sets the task's assigned actor's default dialog node ID. If a node ID is provided as input, it uses that value; otherwise, it uses the current dialog node ID being executed.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID to set. If not provided, uses the current dialog node ID."
            }
          ]
        }
      ],
      "aliases": ["setDefaultNode", "setAsDialogNode", "setAsMyNode"]
    },
    {
      "name": "setMyNodeIf",
      "uses": [
        {
          "description": "Conditionally sets the task's assigned actor's default dialog node ID if a formula evaluates to true (value >= 1). The formula is evaluated in the context of the task's assigned actor. If the condition is met, the actor's dialog node is set to the specified ID.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID to set if the condition is met."
            },
            {
              "name": "strings[1]",
              "description": "A formula expression that determines whether to set the node. If it evaluates to 1 or greater, the node is set."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["setDefaultNodeIf"]
    },
    {
      "name": "clearDialogNode",
      "uses": [
        {
          "description": "Clears the task's assigned actor's default dialog node ID, setting it to an empty string. This removes any assigned dialog behavior from the actor.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "flee",
      "uses": [
        {
          "description": "Makes the task's assigned actor flee from combat, removing them from the zone. The player is awarded 90% of the actor's XP value. A visual effect plays at the actor's position (defaults to 'actor_flee' but can be customized). Combat is delayed until the player acknowledges the flee notification. The XP is added to either the boat or standard XP pool depending on whether the fleeing actor is a boat.",
          "required": [],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The visual effect ID to play when the actor flees. If not provided, defaults to 'actor_flee'."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "enemyFlee",
      "uses": [
        {
          "description": "Makes the entire enemy combat group flee from battle. All actors in the same faction within range of the task's assigned actor are removed from the zone with a flee visual effect. All party members are awarded XP based on the total power level of the fleeing group (90% of 105% of the power level).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "playerFlee",
      "uses": [
        {
          "description": "Makes the player's party flee from combat. All party members become invisible for a duration (starting at 12 seconds, with 3 additional seconds per party member). All actors in the same faction as the task's assigned actor within range are reset: they stop targeting, look at the player, and have their 'auto' dialog node cleared.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enemyMoraleUp",
      "uses": [
        {
          "description": "Increases morale for all actors in the enemy combat group. All actors in the same faction within range of the task's assigned actor receive a morale bonus of +1 for 65 seconds.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enemyMoraleDown",
      "uses": [
        {
          "description": "Decreases morale for all actors in the enemy combat group. All actors in the same faction within range of the task's assigned actor receive a morale penalty of -1 for 65 seconds.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "playerMoraleUp",
      "uses": [
        {
          "description": "Increases morale for all party members. Each party member receives a morale bonus of +1 for 65 seconds.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "crewMorale",
      "uses": [
        {
          "description": "Modifies the player's crew morale by a specified amount. Positive values increase morale, negative values decrease it.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount to modify crew morale by. Can be positive or negative."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "peace",
      "uses": [
        {
          "description": "Makes peace with the enemy combat group and awards XP to the party. All actors in the same faction within range of the task's assigned actor become non-hostile, stop giving XP when killed, and have their 'auto' dialog node changed to 'auto_peaceful'. All party members are awarded XP based on the total power level of the group (90% of 105% of the power level).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "peaceXPrewardOnly",
      "uses": [
        {
          "description": "Awards peace XP to the party without changing enemy behavior. All party members receive XP based on the total power level of the task's assigned actor's combat group (90% of 105% of the power level). Unlike the 'peace' task, this does not make enemies non-hostile or modify their dialog.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveCamXY",
      "uses": [
        {
          "description": "Moves the camera's target position by specified X and Y offsets. The camera smoothly moves to the new position.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The X offset to move the camera by."
            },
            {
              "name": "floats[1]",
              "description": "The Y offset to move the camera by."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveXY",
      "uses": [
        {
          "description": "Sets the task's assigned actor's movement vector to specified X and Y values. The actor will move in that direction.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The X component of the movement vector."
            },
            {
              "name": "floats[1]",
              "description": "The Y component of the movement vector."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveX1",
      "uses": [
        {
          "description": "Makes the task's assigned actor move in the positive X direction (right) with a movement vector of (1, 0).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveY1",
      "uses": [
        {
          "description": "Makes the task's assigned actor move in the positive Y direction (down) with a movement vector of (0, 1).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveXNeg1",
      "uses": [
        {
          "description": "Makes the task's assigned actor move in the negative X direction (left) with a movement vector of (-1, 0).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "moveYNeg1",
      "uses": [
        {
          "description": "Makes the task's assigned actor move in the negative Y direction (up) with a movement vector of (0, -1).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "playerFacingRot",
      "uses": [
        {
          "description": "Sets the player actor's facing rotation to a specified angle. The rotation change triggers a facing rotation effect after a minimal delay.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The rotation angle to set for the player actor's facing direction."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "playerAnim",
      "uses": [
        {
          "description": "Plays a specified animation on the player actor. The animation name is looked up in the actor's animator.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the animation to play on the player."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "facingRot90",
      "uses": [
        {
          "description": "Sets the task's assigned actor's facing rotation to 90 degrees (facing down). The rotation change triggers a facing rotation effect after a minimal delay.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "facingRot180",
      "uses": [
        {
          "description": "Sets the task's assigned actor's facing rotation to 180 degrees (facing left). The rotation change triggers a facing rotation effect after a minimal delay.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "facingRot0",
      "uses": [
        {
          "description": "Sets the task's assigned actor's facing rotation to 0 degrees (facing right). The rotation change triggers a facing rotation effect after a minimal delay.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "facingRot270",
      "uses": [
        {
          "description": "Sets the task's assigned actor's facing rotation to 270 degrees (facing up). The rotation change triggers a facing rotation effect after a minimal delay.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "facingRotPrev",
      "uses": [
        {
          "description": "Sets the task's assigned actor's facing rotation to the previously stored rotation value. This uses the rotation value saved by the setPrevFacingRot task. The rotation change triggers a facing rotation effect after a minimal delay.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setPrevFacingRot",
      "uses": [
        {
          "description": "Stores the task's assigned actor's current facing rotation for later use with facingRotPrev. If a rotation value is provided as input, that value is stored instead of the actor's current rotation.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "A rotation angle to store. If not provided, stores the actor's current facing rotation."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "lookAtTalker",
      "uses": [
        {
          "description": "Makes the player actor turn to face the task's assigned actor (the talker). Calculates the direction from the player to the talker and sets the player's facing rotation accordingly, snapped to the nearest 90-degree angle. If the player is moving, the rotation is reapplied with delays to ensure it sticks.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "lookAtPlayer",
      "uses": [
        {
          "description": "Makes the task's assigned actor turn to face the player. Calculates the direction from the actor to the player and sets the actor's facing rotation accordingly, snapped to the nearest 90-degree angle. If the actor is moving, the rotation is reapplied with delays to ensure it sticks.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setAI_idle",
      "uses": [
        {
          "description": "Sets the task's assigned actor's AI script to 'idle'. This changes the actor's behavior to idle AI patterns.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setAI_idle_flag",
      "uses": [
        {
          "description": "Sets the task's assigned actor's AI script to 'idle_flag'. This changes the actor's behavior to idle flag AI patterns.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "die",
      "uses": [
        {
          "description": "Kills the task's assigned actor by removing their unkillable status and dealing 99999 damage to their HP. This guarantees the actor's death.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "escapeGate",
      "uses": [
        {
          "description": "Makes the task's assigned actor use an escape gate to leave the zone. If in combat mode and the actor is hostile, their XP value is awarded to the player. The 'escapeGate_act' action is executed at the actor's position, and the next combat action is delayed by 1 second.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeTalker",
      "uses": [
        {
          "description": "Removes the task's assigned actor from the zone, deleting them from the game.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setDialogWindowSizeY",
      "uses": [
        {
          "description": "Resizes the NPC dialog window's vertical size to a specified value. This adjusts how tall the dialog window appears on screen.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The vertical size to set for the NPC dialog window."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["setNPCDialogWindowSizeY"]
    },
    {
      "name": "setOnlyNPCDialogWindowSizeY",
      "uses": [
        {
          "description": "Resizes the NPC dialog window's vertical size to a specified value. This adjusts how tall the dialog window appears on screen.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The vertical size to set for the NPC dialog window."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setDialogWindowSizeYAuto",
      "uses": [
        {
          "description": "Automatically resizes the NPC dialog window's vertical size based on its contents. This adjusts the window to fit the dialog text optimally.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setPCDialogWindowSizeY",
      "uses": [
        {
          "description": "Resizes the player character (PC) dialog window's vertical size to a specified value. This adjusts how tall the player's dialog option window appears on screen.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The vertical size to set for the PC dialog window."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resetPCDialogWindowSizeY",
      "uses": [
        {
          "description": "Resets the player character (PC) dialog window's vertical size to its default value.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "showCharstats",
      "uses": [
        {
          "description": "Shows the character stats UI panel. This forces the character statistics display to be visible on screen.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "showQuickslots",
      "uses": [
        {
          "description": "Shows the quickslots UI panel. This forces the quickslot action bar to be visible on screen and un-hides the UI.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "hideQuickslots",
      "uses": [
        {
          "description": "Hides the quickslots UI panel by setting the hideQuickslots flag to false. Note: The implementation appears to have inverse logic and may actually show quickslots.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setCombatUIVisible",
      "uses": [
        {
          "description": "Sets the visibility of the combat UI. This can show or hide combat-related interface elements.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to show (true) or hide (false) the combat UI."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "showEscFactionWindow",
      "uses": [
        {
          "description": "Opens the escape menu showing only the faction information panel (right side). The main escape menu and left info panel are hidden. Sets the dialog to hide when moving to the next node.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "hideEscWindows",
      "uses": [
        {
          "description": "Hides all escape menu windows, including the main menu, left info panel, and right info panel (faction window).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "unlockGem",
      "uses": [
        {
          "description": "Unlocks and enables the help gem feature for the player. If the gem wasn't previously unlocked, plays visual and sound effects at the player's position. The gem becomes immediately usable.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "scoreScreenResponse",
      "uses": [
        {
          "description": "Dynamically determines the next dialog node based on the player's performance metrics. Uses the 'valueThisRun' and 'deathsThisRun' global variables to categorize the run (profit level and deaths), finds matching dialog nodes, and selects the least-used one to provide variety. Increments the usage counter for the selected node. Falls back to 'goToTown' if no matching nodes exist.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "research_report",
      "uses": [
        {
          "description": "Generates a researcher's report on journal entries the player has discovered. Categorizes unsold journal entries by rarity, calculates money and fame rewards for each, and dynamically creates dialog statements describing the findings and rewards. Marks all processed entries as sold to prevent duplicate rewards. Clears and rebuilds the specified dialog node with the generated report.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where the research report will be generated."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cartographer_report",
      "uses": [
        {
          "description": "Generates a cartographer's report on world exploration progress. Calculates the percentage of the world map revealed, compares it to previously paid exploration, and rewards the player with money and fame based on new exploration. Provides bonus rewards if the map is completely revealed (100%). Dynamically generates dialog statements describing progress and rewards.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where the cartographer report will be generated."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "donate",
      "uses": [
        {
          "description": "Allows the player to donate money at the current location, improving favor with the location's faction and fame. If the location has a faction, it increases faction favor and trade fame while decreasing piracy fame. If the location has no faction, it increases trade fame and decreases piracy fame more significantly. Favor increases are based on 0.1% of the donation amount.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount of money to donate."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "jobDialogOptions",
      "uses": [
        {
          "description": "Dynamically generates dialog options for available jobs at the current location. Creates dialog nodes for each quest type (letter delivery, cargo acquisition, enemy defeat, monster slaying, or item retrieval), populating them with quest details, objectives, and rewards. Clears existing options and rebuilds the dialog structure based on the location's available quests.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where job options will be generated."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "refreshLocQuests",
      "uses": [
        {
          "description": "Refreshes the quest board at the current location, clearing existing quests and generating new random quests based on location parameters.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "stealloc",
      "uses": [
        {
          "description": "Transfers control of all ports in the world to the player's faction. This is achieved by investing an extremely large amount at each port location that can change factions, effectively giving the player's faction complete control without affecting wealth levels. Only works if the player has a valid faction set in the 'playerFaction' global variable.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cancelQuest",
      "uses": [
        {
          "description": "Cancels the currently active quest by clearing all quest-related global variables (type, giver, reward, destination, log, goal, royal status, start number, completion status, and log image). Also hides and un-discovers all monster den locations.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cancelRoyalQuest",
      "uses": [
        {
          "description": "Cancels the currently active royal quest if one is active (questRoyal > 0). Clears all quest-related global variables and hides all monster den locations, similar to cancelQuest but with a conditional check for royal quest status.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cleanUpQuest",
      "uses": [
        {
          "description": "Cleans up quest data after quest completion by clearing all quest-related global variables and hiding all monster den locations. This is identical to cancelQuest in implementation but semantically indicates post-quest cleanup rather than cancellation.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "royalJobDialogOptions",
      "uses": [
        {
          "description": "Dynamically generates dialog options for royal quests from the current location's faction. Creates dialog nodes for the active royal quest type (letter delivery, cargo acquisition, fleet defeat, or battle fleet defeat), populating them with quest details, objectives, and acceptance options. Uses global variables 'royalQuestType' and 'royalQuestGoal' to determine quest parameters.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where royal job options will be generated."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "refreshRoyalQuests",
      "uses": [
        {
          "description": "Refreshes the available royal quest, generating a new random royal mission for the player to undertake.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "refreshRoyalTitle",
      "uses": [
        {
          "description": "Resets the player's royal title to an empty string, clearing any earned title.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "investigator_nearestLoc",
      "uses": [
        {
          "description": "Provides information about the nearest undiscovered location from the current position. Searches for undiscovered locations (excluding monster dens), finds the closest one, and stores the answer in a global variable with the investigator's actor ID. If all locations are discovered, provides a message indicating so.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "investigator_tradespecial",
      "uses": [
        {
          "description": "Provides information about the next upcoming trade special event. Reveals which location and which goods will be in high demand for the next special trade opportunity. Stores the answer in a global variable with the investigator's actor ID.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "investigator_slainenemy",
      "uses": [
        {
          "description": "Provides research information on a previously killed enemy type. Searches for actor types the player has killed but hasn't researched yet, and automatically adds the first found journal entry to the player's journal. If all defeated enemies are already researched, provides a message indicating nothing new was found. Stores the answer in a global variable with the investigator's actor ID.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "investigator_fleetpos",
      "uses": [
        {
          "description": "Provides fleet tracking information for a specified faction. Makes the specified faction's fleet movements visible on the map for 30 in-game days by setting a tracking global variable. Stores the answer in a global variable with the investigator's actor ID.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The faction ID whose fleet movements should be tracked."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "investigator_setupportselect",
      "uses": [
        {
          "description": "Generates dialog options showing investment opportunities in a specific quadrant of the world. Based on provided coordinates representing a quadrant direction, finds all discovered ports in that quadrant and identifies which items or actors become available at higher investment levels. Creates dialog options describing what can be unlocked by investing at each location.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where investment opportunities will be listed."
            },
            {
              "name": "floats[0]",
              "description": "The X component of the quadrant direction (-1, 0, or 1)."
            },
            {
              "name": "floats[1]",
              "description": "The Y component of the quadrant direction (-1, 0, or 1)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setupBuyShipDialog",
      "uses": [
        {
          "description": "Generates dialog options for purchasing ships at the current location's shipwright. Clears existing options and creates a dialog option for each ship type available for purchase, including ship stats in tooltips. Arranges options in columns if there are more than 4 ships available.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where ship purchase options will be generated."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "crash",
      "uses": [
        {
          "description": "Intentionally crashes the game by accessing a non-existent dictionary key. This is a debug/testing task that causes an immediate exception.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buyShip",
      "uses": [
        {
          "description": "Purchases a ship of the specified type for the player. Deducts the ship's value from the player's money, creates the ship with appropriate settings (non-hostile, controllable, player faction), and adds it to the player's fleet. If the player has no fleet yet, initializes a new fleet with this ship. Plays a success sound effect.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor type ID of the ship to purchase."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "giveShip",
      "uses": [
        {
          "description": "Gives the player a free ship of the specified type. Creates the ship with appropriate settings (non-hostile, controllable, player faction) and adds it to the player's fleet. If the player has no fleet yet, initializes a new fleet with this ship. Increments a global counter for ship boons. Allows custom ship ID specification. Plays a success sound effect.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor type ID of the ship to give."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "A custom unique ID for the ship. If not provided, generates an ID based on the ship type and boon counter."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "renameShip",
      "uses": [
        {
          "description": "Opens the character editor UI to rename the most recently added ship in the player's fleet. The editor cannot be closed until a name is entered.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addToFleet",
      "uses": [
        {
          "description": "Adds the task's assigned actor (a moored ship) to the player's fleet. Clears the 'boat_unmoor' dialog node if set, adds the actor to the party, and removes it from the zone without removing it from the party data.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addToFleet_andSwapWithFleet_slot",
      "uses": [
        {
          "description": "Swaps a moored ship with a ship currently in the player's fleet at the specified slot index. The fleet ship is removed, its crew is transferred to the moored ship, the moored ship is added to the fleet, and the original fleet ship is placed back in the zone as a moored ship at a travel point. The new ship takes the original ship's position in the fleet order.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The fleet slot index (0-based) of the ship to swap out."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeFromParty_slot",
      "uses": [
        {
          "description": "Removes a party member at the specified index from the active party and places them in the zone as a hireable crew member. Fully heals the actor, unequips all their items (moving them to stash), sets their dialog to 'crew_rehire', and positions them at a crew travel point. If no valid position is found, displays an error message instead.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The party index (0-based) of the actor to remove."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "researchFromFleet_slot",
      "uses": [
        {
          "description": "Submits a ship from the player's fleet for research at the current location's shipwright. Validates the ship isn't already being researched or available, checks shipwright capacity, then removes the ship from the fleet, transfers its cargo to the player's cargo hold, deducts the ship's value from money, and adds it to the location's research queue (450 days). Changes dialog node based on validation results.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The fleet slot index (0-based) of the ship to research."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeFromFleet_slot",
      "uses": [
        {
          "description": "Removes a ship from the player's fleet at the specified slot and places it in the zone as a moored ship. Positions the ship near a designated travel point, sets it to 'boat_unmoor' dialog mode with special idle animation, and clears its AI. If no valid position is found, displays an error message.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The fleet slot index (0-based) of the ship to remove."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterPort",
      "uses": [
        {
          "description": "Enters the nearest port location to the player's current position, searching within 4 tiles. Only considers locations marked as ports.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "enterLocation",
      "uses": [
        {
          "description": "Enters the nearest location to the player's current position, searching within 4 tiles. Considers all location types, not just ports.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "buyLocationFood",
      "uses": [
        {
          "description": "Purchases food from the current location's cafe for the entire crew. Buys one food item per crew member, deducts the cost, improves crew morale by the food's power value, and applies any special effects (like wind buffs) to the fleet or crew. Moves the party to a designated travel point and records the food type eaten.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "invest",
      "uses": [
        {
          "description": "Invests money at the current location to increase its wealth level and potentially change its faction allegiance. If the player has a faction, the investment increases player faction favor and may decrease the current faction's favor. If the location can change factions and sufficient investment is made, it may switch to the player's faction. Also increases trade fame and decreases piracy fame.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount of money to invest."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modTalkerFavor",
      "uses": [
        {
          "description": "Modifies favor with the faction of the task's assigned actor (the talker). Only works if the actor belongs to a valid faction.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount to modify faction favor by. Can be positive or negative."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modLocFavor",
      "uses": [
        {
          "description": "Modifies favor with the faction that controls the current location.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount to modify faction favor by. Can be positive or negative."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modLocWealthLevel",
      "uses": [
        {
          "description": "Modifies the wealth level of the current location. Wealth level affects what items and services become available. Values are clamped between 0 and 100, and changing wealth may unlock new items.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount to modify the location's wealth level by. Can be positive or negative."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setLocDiscoverable",
      "uses": [
        {
          "description": "Makes a specific location discoverable by the player, allowing it to appear on the map or be found through exploration.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The location ID to make discoverable."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "createMonsterDenNearby",
      "uses": [
        {
          "description": "Creates a new monster den location near the current location. Finds the nearest existing monster den location, creates a new one at that position with specified level data, makes it discoverable, and replaces the old location in the world data.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The level data string defining the monster den's contents and difficulty."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "bezoar_setupportselect",
      "uses": [
        {
          "description": "Generates fast travel dialog options to discovered port locations in a specific quadrant of the world, excluding the current location and a specific port. Based on provided coordinates representing a quadrant direction, finds all discovered ports in that quadrant and creates dialog options that teleport to each location. This is specifically for the bezoar fast travel system.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where fast travel options will be listed."
            },
            {
              "name": "floats[0]",
              "description": "The X component of the quadrant direction (-1, 0, or 1)."
            },
            {
              "name": "floats[1]",
              "description": "The Y component of the quadrant direction (-1, 0, or 1)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cameraAtPlayer",
      "uses": [
        {
          "description": "Points the camera at the player's current position.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cameraAtTalker",
      "uses": [
        {
          "description": "Points the camera at the task's assigned actor's (the talker's) current position.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cameraAtPoint",
      "uses": [
        {
          "description": "Points the camera at a specific travel point identified by two coordinates. The camera is locked to this position.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The first coordinate of the travel point (travel point type)."
            },
            {
              "name": "floats[1]",
              "description": "The second coordinate of the travel point (travel point index)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "cameraAtPointLock",
      "uses": [
        {
          "description": "Points the camera at a specific travel point and locks it there indefinitely. The camera cannot be moved by the player until the lock is cleared.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The first coordinate of the travel point (travel point type)."
            },
            {
              "name": "floats[1]",
              "description": "The second coordinate of the travel point (travel point index)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "talkToOtherActor",
      "uses": [
        {
          "description": "Initiates dialog with another actor at the same position as the task's assigned actor. Prioritizes non-incapacitated actors and excludes the current talker.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "giveItem",
      "uses": [
        {
          "description": "Gives the player a specified quantity of an item. The item is added to the player's inventory or appropriate storage.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The item type ID to give."
            },
            {
              "name": "floats[0]",
              "description": "The quantity of the item to give."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "triggerEffect",
      "uses": [
        {
          "description": "Triggers a game effect after a specified delay using a simple effect name.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The effect name to trigger."
            },
            {
              "name": "floats[0]",
              "description": "The delay in seconds before triggering the effect."
            }
          ],
          "optional": []
        },
        {
          "description": "Triggers a complex game effect with multiple parameters after a specified delay. This variant accepts up to 6 string parameters for the effect system.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The delay as a string (will be parsed to float)."
            },
            {
              "name": "strings[1]",
              "description": "The effect name to trigger."
            },
            {
              "name": "strings[2]",
              "description": "First effect parameter (will be parsed to float)."
            },
            {
              "name": "strings[3]",
              "description": "Second effect parameter."
            },
            {
              "name": "strings[4]",
              "description": "Third effect parameter (will be parsed to float)."
            },
            {
              "name": "strings[5]",
              "description": "Fourth effect parameter."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "dialogEffect",
      "uses": [
        {
          "description": "Triggers a dialog-specific effect after a specified delay.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog effect name to trigger."
            },
            {
              "name": "floats[1]",
              "description": "The delay in seconds before triggering the effect."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "nextDialogNode_hideWindow",
      "uses": [
        {
          "description": "Sets a flag that causes the dialog window to hide when transitioning to the next dialog node.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "closeCharEdit",
      "uses": [
        {
          "description": "Closes the character editor UI window.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "gameOver",
      "uses": [
        {
          "description": "Triggers the game over sequence, ending the current game.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "autosaveASAP",
      "uses": [
        {
          "description": "Flags the game to create an autosave as soon as possible.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "speechFX",
      "uses": [
        {
          "description": "Plays the speech sound effect for the task's assigned actor.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "fxOnPlayerRank",
      "uses": [
        {
          "description": "Plays a visual effect at the player's rank display in the escape menu faction info panel.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The visual effect ID to play."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "returnMissingPartyMembers",
      "uses": [
        {
          "description": "Returns any party members who got separated back to the party.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modPlayerFactionFavor",
      "uses": [
        {
          "description": "Modifies favor with the player's chosen faction by a specified amount.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount to modify faction favor by. Can be positive or negative."
            }
          ],
          "optional": []
        },
        {
          "description": "Modifies favor with the player's chosen faction using a formula calculation.",
          "required": [
            {
              "name": "strings[0]",
              "description": "A formula expression that calculates the favor modification amount."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "modTalkerFactionFavor",
      "uses": [
        {
          "description": "Modifies favor with the faction of the actor the player is currently talking to.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount to modify faction favor by. Can be positive or negative."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "refreshStatModifiers",
      "uses": [
        {
          "description": "Refreshes stat modifiers for all actors in the current zone. This recalculates equipment bonuses, temporary effects, and other stat modifications for every actor present.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "storeBoost",
      "uses": [
        {
          "description": "Stores the player's current boost state if they are in auto-move mode. This preserves the boost for later use.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "recoverOverboardPartyActors",
      "uses": [
        {
          "description": "Recovers party members who went overboard during combat. Removes incapacitated or destroyed ships from the fleet, then adds all overboard party members back to the flagship's crew. Clears the overboard list.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "talkToCommodore",
      "uses": [
        {
          "description": "Initiates dialog with the commodore (leader) of the fleet that the task's assigned actor belongs to. If not already talking to the commodore, switches to dialog with them.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "talkToFirstMate",
      "uses": [
        {
          "description": "Initiates dialog with the first mate character, whose actor ID is stored in the 'firstmate' global variable. If not already talking to the first mate, switches to dialog with them.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "talkToFlagship",
      "uses": [
        {
          "description": "Initiates dialog with the flagship of the fleet that the task's assigned actor belongs to. Handles special cases for factionless fleets and the Lagan character. If the fleet has no valid faction and isn't Lagan, ends dialog silently.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setupFleetLooting",
      "uses": [
        {
          "description": "Prepares fleet looting after combat victory. Consolidates cargo from all defeated enemy and assist fleet ships into the enemy fleet's cargo. Collects items from boat zones. Triggers achievements for defeating specific fleet types (SM fleet, merchant fleet, battle fleet, elite fleet). Clears special zone status.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "captureShips_afterCombat",
      "uses": [
        {
          "description": "Generates dialog options for capturing defeated enemy ships after combat. Lists all non-incapacitated enemy and assist fleet ships with their stats, allowing the player to select which ships to capture. Displays the player's fleet window for reference. Updates dialog dynamically based on the number of available ships.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where ship capture options will be generated."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "captureShipNum",
      "uses": [
        {
          "description": "Captures a specific defeated ship by index and adds it to the player's fleet. The ship is converted to player faction, crew is cleared, hostile status removed, and stats are slightly reduced (HP -2). The ship is removed from the enemy/assist fleet and added to the player's fleet with a new ID. Limited to 9 ships total in player fleet.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The index (0-based) of the ship to capture from the list of available ships."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "lootFleet_afterCombat",
      "uses": [
        {
          "description": "Opens the looting interface after fleet combat. Consolidates enemy and assist fleet cargo, then displays two container UIs side-by-side: the player's cargo and the enemy fleet's cargo. Enables item transfer mode with button cursor. Resizes dialog window for the looting interface.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "openFleetWindow",
      "uses": [
        {
          "description": "Opens the fleet management window showing the player's fleet composition and stats.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "openFleetWindow_delay",
      "uses": [
        {
          "description": "Opens the fleet management window after a 0.1 second delay.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "openFleetWindow_talker",
      "uses": [
        {
          "description": "Opens the fleet management window showing the fleet that the task's assigned actor belongs to.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "closeFleetWindow",
      "uses": [
        {
          "description": "Closes the fleet management window.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "showFleetInfo",
      "uses": [
        {
          "description": "Generates dialog options displaying detailed information about each ship in the task's assigned actor's fleet. Creates a dialog option for each ship with its stats in a tooltip. Includes a fleet description in the dialog statements.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID where fleet information will be displayed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "repairFleet_pay",
      "uses": [
        {
          "description": "Repairs the player's entire fleet, deducting the repair cost from the player's money. The cost is calculated with favor reduction applied.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "repairFleet_free",
      "uses": [
        {
          "description": "Repairs the player's entire fleet at no cost.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "help",
      "uses": [
        {
          "description": "Displays console command help. If no command name is provided, lists all available console commands. If a command name is provided, shows detailed help for that specific command.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The name of the command to get help for. If not provided, lists all commands."
            }
          ]
        }
      ],
      "aliases": ["h"]
    },
    {
      "name": "csave",
      "uses": [
        {
          "description": "Creates a console save of the current game state. This is a debug/development command for manual save creation.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The name to use for the save file. If not provided, defaults to 'consoleSave'."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "testitemload",
      "uses": [
        {
          "description": "Debug command that spawns one of a specified item type on every tile in the current zone. If no item is specified, spawns 'toyboat' items. This is used for testing item placement.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The item type ID to spawn. If not provided, defaults to 'toyboat'."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "howmanyitems",
      "uses": [
        {
          "description": "Debug command that counts the total number of items in the current zone. If an item type is specified, counts only that type; otherwise counts all items. Displays the result in the debug console.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The item type ID to count. If not provided, counts all items."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "convertRGB",
      "uses": [
        {
          "description": "Debug command that converts a PNG image from the desktop to RGB palette scheme and saves it back to the desktop with '_rgb' suffix.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The filename (without .png extension) of the image on the desktop to convert."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "screenshot",
      "uses": [
        {
          "description": "Takes a screenshot at specified coordinates and saves it to the desktop. With no color palette specified, saves the raw screenshot.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The X coordinate for the screenshot center."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate for the screenshot center."
            }
          ],
          "optional": []
        },
        {
          "description": "Takes a screenshot with RGB color palette conversion at specified coordinates. Converts to RGB palette scheme and applies color swapping.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The color palette to apply."
            },
            {
              "name": "floats[0]",
              "description": "The X coordinate for the screenshot center."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate for the screenshot center."
            }
          ],
          "optional": []
        },
        {
          "description": "Takes a screenshot of a special zone with RGB conversion and color swapping. Loads a specific zone file for the screenshot.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The color palette to apply."
            },
            {
              "name": "strings[1]",
              "description": "The special zone ID to load for the screenshot."
            },
            {
              "name": "floats[0]",
              "description": "The X coordinate for the screenshot center."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate for the screenshot center."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["screenpic"]
    },
    {
      "name": "anim",
      "uses": [
        {
          "description": "Debug command that plays a specified animation on the player actor and switches to travel mode.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The animation name to play."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "fullheal",
      "uses": [
        {
          "description": "Fully heals all party members, restoring HP and removing incapacitation.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["rest"]
    },
    {
      "name": "testboatanim",
      "uses": [
        {
          "description": "Debug command that tests boat animations by reverting to previous mode and triggering boarding special zone timers.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "bighead",
      "uses": [
        {
          "description": "Toggles big head mode (visual effect). If a boolean is provided and true, enables big head mode regardless of current state.",
          "required": [],
          "optional": [
            {
              "name": "bools[0]",
              "description": "If true, forces big head mode on instead of toggling."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "screenscale",
      "uses": [
        {
          "description": "Sets a forced screen scale value. Use -1 to disable forced scaling and return to automatic scaling.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The screen scale multiplier to force. Use -1 to disable forced scaling."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "res",
      "uses": [
        {
          "description": "Debug command that sets the resolution to 1920x959 in windowed mode.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "go",
      "uses": [
        {
          "description": "Debug command that loads a zone by name, optionally at specific coordinates. Sets console entry flag.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The zone name/ID to load."
            }
          ],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The X coordinate to spawn at."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate to spawn at."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "gob",
      "uses": [
        {
          "description": "Debug command that loads a zone by name in build mode, optionally at specific coordinates. Sets console entry and build mode flags.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The zone name/ID to load."
            }
          ],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The X coordinate to spawn at."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate to spawn at."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "p",
      "uses": [
        {
          "description": "Debug command that creates and loads a procedural zone with specified level data. The zone is prefixed with 'p_'.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The level data ID to use for procedural generation."
            }
          ],
          "optional": [
            {
              "name": "bools[0]",
              "description": "If false, creates the zone without entering it. Default is true."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "world",
      "uses": [
        {
          "description": "Debug command that creates and loads a procedural world from specified level data. Generates a full world map and enters at coordinates (12, 12).",
          "required": [
            {
              "name": "strings[0]",
              "description": "The level data ID to use for world generation."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["w"]
    },
    {
      "name": "loads",
      "uses": [
        {
          "description": "Debug command that loads a system save by name.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The system save name to load."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "z",
      "uses": [
        {
          "description": "Debug command that lists all zone files in the ZoneData directory, excluding procedural zones, set pieces, and special zones. Optionally filters by prefix.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "A prefix filter to match zone names starting with 'z' + this value."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "sp",
      "uses": [
        {
          "description": "Debug command that lists all set piece zone files in the SetPieces directory. Optionally filters by prefix.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "A prefix filter to match set piece names starting with 'sp' + this value."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "bz",
      "uses": [
        {
          "description": "Debug command that lists all zone files starting with 'bz' prefix.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "listfx",
      "uses": [
        {
          "description": "Debug command that lists all visual effects (FX). If an effect name is provided, shows detailed info for that effect; otherwise lists all effect names.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The FX name to get detailed information about."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "listav",
      "uses": [
        {
          "description": "Debug command that lists all actor values (stats/attributes). If an actor value name is provided, shows detailed info for that value; otherwise lists all non-rank/XP actor values.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The actor value name to get detailed information about."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "k",
      "uses": [
        {
          "description": "Debug command that displays the kill count for all actor types the player has defeated.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "kill",
      "uses": [
        {
          "description": "Debug command that kills all hostile non-controllable actors in the current zone, except invincible ones and any specified by ID. Deals 99999 damage to each.",
          "required": [],
          "optional": [
            {
              "name": "strings[0+]",
              "description": "Actor IDs to exclude from the kill command."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "killall",
      "uses": [
        {
          "description": "Debug command that kills all non-controllable actors in the current zone, except invincible ones. Deals 99999 damage to each, regardless of hostile status.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "killcrew",
      "uses": [
        {
          "description": "Debug command that kills all crew members of all non-controllable actors with crew. Deals 99999 damage to each crew member and applies the crewless effect to the parent actor.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "reviveall",
      "uses": [
        {
          "description": "Debug command that revives all incapacitated actors in the current zone, except any specified by ID. Fully heals them and clears busy timers.",
          "required": [],
          "optional": [
            {
              "name": "strings[0+]",
              "description": "Actor IDs to exclude from revival."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "win",
      "uses": [
        {
          "description": "Debug command that immediately ends combat mode with a victory.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setk",
      "uses": [
        {
          "description": "Debug command that sets or increments the kill count for a specific actor type.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor type ID to set kill count for."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The kill count value to set. If not provided, increments by 1."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "set",
      "uses": [
        {
          "description": "Debug command that sets a global variable. Can set string or numeric values. Refreshes royal quest after setting.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The global variable name to set."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The string value to set. If both strings[1] and floats[0] are provided, strings[1] takes precedence."
            },
            {
              "name": "floats[0]",
              "description": "The numeric value to set. Only used if strings[1] is not provided."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "get",
      "uses": [
        {
          "description": "Debug command that retrieves and displays a global variable value or evaluates a formula. If no variable is specified, lists all non-dialog global variables.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The global variable name to retrieve, or a formula expression to evaluate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "formulas",
      "uses": [
        {
          "description": "Debug command that displays all global formula definitions with their formula strings.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "calc",
      "uses": [
        {
          "description": "Debug command that evaluates a formula expression and displays the result.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The formula expression to calculate."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["math"]
    },
    {
      "name": "resetfog",
      "uses": [
        {
          "description": "Debug command that resets fog of war in the current zone, making all tiles unknown again.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["fog"]
    },
    {
      "name": "cfog",
      "uses": [
        {
          "description": "Debug command that clears fog of war in the current zone, revealing all tiles. On the world map (eral), also discovers all discoverable locations and refreshes the minimap.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["clearfog"]
    },
    {
      "name": "hidemapicon",
      "uses": [
        {
          "description": "Debug command that hides all location icons on the world map (eral) by marking them as undiscovered.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["hidemapicons"]
    },
    {
      "name": "clearitems",
      "uses": [
        {
          "description": "Debug command that removes all items from every tile in the current zone. If an item ID is specified, removes only that item type.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The item type ID to remove. If not provided, removes all items."
            }
          ]
        }
      ],
      "aliases": ["removeitems"]
    },
    {
      "name": "clearroofs",
      "uses": [
        {
          "description": "Debug command that removes all roof items from every tile in the current zone.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["clearroof"]
    },
    {
      "name": "deleteZone",
      "uses": [
        {
          "description": "Debug command that permanently deletes a zone's data file. Only works when in console mode. WARNING: This is irreversible.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The zone ID to permanently delete."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "forget",
      "uses": [
        {
          "description": "Debug command that forgets (resets) a zone's saved state, reverting it to its initial condition. Also clears trigger flags for that zone.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The zone ID to forget/reset."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resetloc",
      "uses": [
        {
          "description": "Debug command that resets a specific location's state to its initial values.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The location ID to reset."
            }
          ],
          "optional": []
        }
      ],
      "aliases": ["resetlocation"]
    },
    {
      "name": "resetlocations",
      "uses": [
        {
          "description": "Debug command that resets all locations to their initial states.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "refreshloc",
      "uses": [
        {
          "description": "Debug command that adds any missing locations to the game world (used when new locations are added to the game).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resetprocgen",
      "uses": [
        {
          "description": "Debug command that resets all procedurally generated locations to regenerate them with new content.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resetwp",
      "uses": [
        {
          "description": "Debug command that forces a refresh of AI waypoints in the current zone.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["resetwaypoint", "resetwaypoints"]
    },
    {
      "name": "build",
      "uses": [
        {
          "description": "Debug command that switches to build mode for level editing.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["b"]
    },
    {
      "name": "overseer",
      "uses": [
        {
          "description": "Debug command that switches to overseer mode (top-down strategy view).",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "gm",
      "uses": [
        {
          "description": "Debug command that enables god mode for the player, setting HP/MP to 999, defenses to 99, and granting 9 ranks in all abilities from all classes.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["godmode"]
    },
    {
      "name": "abil",
      "uses": [
        {
          "description": "Debug command that grants the player character ranks in all abilities from all selectable classes in the game. This iterates through every class that can be selected by players and adds ranks to every action/ability in those classes. By default, this grants 9 ranks in each ability.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The number of ranks to grant in each ability. This value is cast to an integer before being applied. If not provided, defaults to 9 ranks."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "xp",
      "uses": [
        {
          "description": "Awards experience points to all party members. Each party actor in the current party receives the same amount of XP. This XP is applied to the party member's currently active class.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The amount of experience points to award to each party member. If not provided, defaults to 9999 XP."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "crewxp",
      "uses": [
        {
          "description": "Awards experience points to all crew members. Unlike the 'xp' task which only affects active party members, this command awards XP to all crew members in the player's roster.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount of experience points to award to all crew members."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "allxp",
      "uses": [
        {
          "description": "Debug command that awards experience points to all party members across all selectable classes. Unlike the 'xp' task which only awards XP to each party member's currently active class, this command awards the same amount of XP to every selectable class for each party member. This allows party members to gain levels in all available classes simultaneously. By default, this awards 9999 XP per class.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The amount of experience points to award to each party member in each class. This value is cast to an integer before being applied. If not provided, defaults to 9999 XP per class."
            }
          ]
        }
      ],
      "aliases": ["xpall"]
    },
    {
      "name": "xpcosts",
      "uses": [
        {
          "description": "Debug command that displays the total XP cost for all abilities in each selectable class. This command calculates and shows on screen the sum of all XP costs for every action/ability within each class that can be selected by players. This is useful for balancing and debugging class progression costs. This command does not modify any game state, only displays information.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["xpcost"]
    },
    {
      "name": "testskillcost",
      "uses": [
        {
          "description": "Debug command that calculates and displays XP costs for skill progression using a custom formula. This command computes costs for 12 progression levels using the formula: ((base + level * increment) * multiplier^level). The results are displayed as a comma-separated list on screen. This is useful for testing and balancing XP progression curves for skills and abilities. If fewer than 3 float values are provided, an error message is displayed instead.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The base cost value. This is the starting point for the cost calculation, applied at level 0. The value is cast to an integer before being used in the formula."
            },
            {
              "name": "floats[1]",
              "description": "The linear increment per level. This value is added for each level and determines how much the base cost grows linearly with each progression level."
            },
            {
              "name": "floats[2]",
              "description": "The exponential multiplier. This value is raised to the power of the current level, creating exponential growth in the cost curve. Values greater than 1 create increasing costs, while values less than 1 create decreasing costs."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "allclass",
      "uses": [
        {
          "description": "Debug command that unlocks all selectable classes in the game for the player. This command iterates through every class that can be selected by players and unlocks them by setting the appropriate global variables and adding them to the player's known classes list. For classes with formula requirements that reference global variables (starting with 'g:'), it sets those specific global variables. For other classes, it sets a 'class_' prefixed global variable. This allows immediate access to all classes without meeting normal unlock requirements.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["allc"]
    },
    {
      "name": "gp",
      "uses": [
        {
          "description": "Adds money to the player's current funds.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The amount of money to add to the player's funds. If not provided, defaults to 500."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "fl",
      "uses": [
        {
          "description": "Debug command that toggles the FPS (frames per second) limit on or off. This allows the game to run uncapped or return to its normal frame rate limit. The toggle takes effect as soon as possible.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["tfl"]
    },
    {
      "name": "tm",
      "uses": [
        {
          "description": "Debug command that toggles the visibility of menus on or off. This hides or shows all menu UI elements in the game.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "tt",
      "uses": [
        {
          "description": "Debug command that toggles whether tooltips are drawn on screen. This can be used to hide or show all tooltip UI elements.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "tdm",
      "uses": [
        {
          "description": "Debug command that toggles the display of damage numbers. When enabled, damage numbers appear over characters when they take or deal damage. When disabled, these numbers are hidden.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "tq",
      "uses": [
        {
          "description": "Debug command that toggles the visibility of quickslots and character stats UI elements. This hides or shows these interface components completely.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "tc",
      "uses": [
        {
          "description": "Debug command that toggles combat mode on or off. This enables or disables the combat system entirely. The current state of combat mode is displayed on screen when toggled.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "tcui",
      "uses": [
        {
          "description": "Debug command that toggles the visibility of the combat UI. When enabled, combat UI elements are hidden all the time. When disabled, they display normally.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "tw",
      "uses": [
        {
          "description": "Debug command that toggles the drawing of wires (debug visualization lines). This visual debugging aid helps visualize connections and paths in the game.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "realtime",
      "uses": [
        {
          "description": "Debug command that toggles realtime mode on or off. This switches between turn-based and realtime gameplay modes.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "hideparty",
      "uses": [
        {
          "description": "Debug command that makes all party members invisible. This sets each party actor's invisibility timer to a very high value (99999 seconds) and marks them as not visible. This is useful for testing scenarios without the party being visible on screen.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["hidep"]
    },
    {
      "name": "mpos",
      "uses": [
        {
          "description": "Debug command that toggles the display of mouse position coordinates on screen. This shows or hides the current mouse cursor position, useful for debugging UI and positioning.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "rankstyle",
      "uses": [
        {
          "description": "Debug command that toggles the visual style used to display ability ranks. This switches between using notches (visual marks) or another display method for showing skill ranks.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "clearjournal",
      "uses": [
        {
          "description": "Debug command that clears all journal entries and resets them to the default starting entries. This removes all learned journal entries from the player's knowledge and then re-adds only the default known journal entries that players start with. Alerts for new entries are suppressed during this reset.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["clearj"]
    },
    {
      "name": "filljournal",
      "uses": [
        {
          "description": "Debug command that unlocks all journal entries in the game. This adds every journal entry that exists in the game data to the player's known journal entries list, giving immediate access to all lore and information. After adding all entries, the journal is sorted by category for organization.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["fillj"]
    },
    {
      "name": "dm",
      "uses": [
        {
          "description": "Debug command that toggles debug mode on or off. Debug mode enables various debugging features and information displays. The current state of debug mode is displayed on screen when toggled.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": ["debug"]
    },
    {
      "name": "debugMessage",
      "uses": [
        {
          "description": "Debug command that displays a custom message on screen. This allows showing arbitrary debug information during development or testing. The message can have a category label and content, or just content with a default 'Debug' category.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The message content to display. If this is the only string provided, it appears under the 'Debug' category. If a second string is provided, this becomes the category label instead."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "The actual message content to display. When provided, strings[0] is used as the category label and this becomes the message text."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "errors",
      "uses": [
        {
          "description": "Debug command that toggles the display of error messages on screen. When enabled, error messages are shown. When disabled, they are hidden. The current state is displayed on screen when toggled.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resize",
      "uses": [
        {
          "description": "Debug command that changes the game's display resolution. This sets the target width and height for the game window or display mode. Both dimensions must be provided for the resize to take effect.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The new display width in pixels. This value is cast to an integer."
            },
            {
              "name": "floats[1]",
              "description": "The new display height in pixels. This value is cast to an integer."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setBPC",
      "uses": [
        {
          "description": "Debug command that modifies body part connection data for character body structures. This is used to adjust how body parts are connected and positioned on character models. The command sets connection data including rotation and offset for a specific body part connection.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the body structure to modify. This identifies which character body structure definition to update."
            },
            {
              "name": "strings[1]",
              "description": "The specific body part connection to modify within the body structure. This identifies which connection point on the body structure should be updated."
            },
            {
              "name": "floats[0]",
              "description": "The X coordinate offset for the body part connection position. This adjusts the horizontal positioning of the connected body part."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate offset for the body part connection position. This adjusts the vertical positioning of the connected body part."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setBP",
      "uses": [
        {
          "description": "Debug command that sets the origin point for a body part in a character body structure. This defines the base position from which a body part is drawn and positioned on a character model. Unlike setBPC which modifies connections, this sets the fundamental origin point of the body part itself.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The ID of the body structure to modify. This identifies which character body structure definition to update."
            },
            {
              "name": "strings[1]",
              "description": "The specific body part within the body structure whose origin should be set. This identifies which part of the body structure to update."
            },
            {
              "name": "floats[0]",
              "description": "The X coordinate for the body part origin point. This sets the horizontal position of the body part's origin."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate for the body part origin point. This sets the vertical position of the body part's origin."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "time",
      "uses": [
        {
          "description": "Debug command that advances or rewinds the game's world time. This modifies the current time in the game world by the specified amount. Positive values advance time forward, while negative values move it backward. After changing the time, the travel info window is refreshed to reflect the new time.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The amount of time to add to the world time. The units depend on the game's time system. Positive values advance time, negative values rewind it."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "wind",
      "uses": [
        {
          "description": "Debug command that sets the wind direction (rotation) in the game world. This affects environmental effects and potentially sailing mechanics. The wind direction is specified as a rotation value.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The rotation value for the wind direction. This sets the angle of the wind in the game world."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "weather",
      "uses": [
        {
          "description": "Changes the current weather in the game world. This immediately sets the weather to the specified type, affecting visual effects and potentially gameplay mechanics.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The weather type to set. Valid values are: none, rain, storm, snow, gale, krill"
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resetachievements",
      "uses": [
        {
          "description": "Debug command that resets all Steam achievements and statistics. This clears all achievement progress and stats on Steam, returning them to their initial unlocked state. This command only functions when the Steam API is initialized.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "howmany",
      "uses": [
        {
          "description": "Debug command that counts and displays the total number of items or actors in the current zone. When given an item or actor ID, it counts instances of that specific type. When no ID is provided, it counts all items on all tiles. The result is displayed on screen.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The ID of the item or actor type to count. If provided, only instances of this specific item or actor type are counted. For items, it counts across all tiles. For actors, it counts matching actors in the zone. If not provided, all items in the zone are counted."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "howmanyWorld",
      "uses": [
        {
          "description": "Debug command that searches all zone data files across the entire game world and counts occurrences of a specific string pattern. This is useful for finding how many times an item, actor, or other game element appears across all zones. The command displays both per-zone counts (for zones where the pattern appears) and a total count. If no search string is provided, it automatically detects what the mouse is hovering over (either an actor or item) and searches for that.",
          "required": [],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The search pattern to count across all zone data files. This is typically formatted as part of a data definition (e.g., 'Item] ID=torch' or 'actorTypeID=merchant'). If not provided, the command examines the tile under the mouse cursor and automatically determines the search pattern from whatever actor or item is at that location."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setMainMenuUIVisible",
      "uses": [
        {
          "description": "Sets the visibility state of the main menu UI. This controls whether the main menu interface is shown or hidden. Optionally, this can also enable the main menu background and UI manager rendering.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to make the main menu visible (true) or hidden (false). This controls the primary visibility state of the main menu interface."
            },
            {
              "name": "bools[1]",
              "description": "Whether to enable the main menu background and UI manager drawing. When true, both the main menu background and the UI manager are set to draw. When false, these remain in their current state."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "markTrainerLocDone",
      "uses": [
        {
          "description": "Marks the current location's trainer (book) icons as completed. This updates the location's tooltip icons to show progression through trainer activities. Book icons progress from unmarked to 'done' to 'done_done' states, indicating different levels of completion. This is typically used when the player completes training activities at a location. This only affects the current location where the task is executed.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "markDojoLocDone",
      "uses": [
        {
          "description": "Marks the current location's dojo (muscle) icon as completed. This updates the location's tooltip icon from 'sign_icon_muscle' to 'sign_icon_muscle_done', indicating that the player has completed the dojo's training. This is typically used when the player finishes combat training or challenges at a dojo location. This only affects the current location where the task is executed.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "createTreasureMap",
      "uses": [
        {
          "description": "Creates a treasure map image centered on a specific tile coordinate in the overworld. This command generates a styled screenshot of the overworld area around the specified location, applies visual processing (RGB palette conversion, color adjustments, adds an X mark), frames it, and saves it as the current treasure map. The treasure location coordinates are also stored in global variables. This is used to create treasure hunt quests where players must find specific locations in the game world.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The X coordinate of the treasure location on the overworld map. This is the center point of the area that will be captured in the treasure map image. The value is converted to a TileCoord and stored in the 'treasureTCX' global variable."
            },
            {
              "name": "floats[1]",
              "description": "The Y coordinate of the treasure location on the overworld map. This is the center point of the area that will be captured in the treasure map image. The value is converted to a TileCoord and stored in the 'treasureTCY' global variable."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "tMapTest",
      "uses": [
        {
          "description": "Debug command for testing treasure map generation. This creates a series of treasure map screenshots across the overworld by repeatedly calling itself with advancing coordinates. It loads the overworld zone ('eral') as a special zone, cleans it up by removing certain items and actors, and generates screenshots at regular intervals. By default, it starts at position (100, 100) and advances 20 tiles at a time horizontally, wrapping to the next row when reaching X coordinate 700. This is used for batch-generating map previews or testing the treasure map rendering system.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The starting X coordinate for the treasure map test screenshot. If not provided, defaults to 100. The command will automatically advance this coordinate for the next iteration."
            },
            {
              "name": "floats[1]",
              "description": "The starting Y coordinate for the treasure map test screenshot. If not provided, defaults to 100. The command will automatically advance this coordinate when wrapping to the next row."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "addTreasuresToZone",
      "uses": [
        {
          "description": "Adds a buried treasure chest to the zone at the coordinates stored in the global variables 'treasureTCX' and 'treasureTCY'. The treasure chest is a shipwreck location that contains random gold (90-220), a cargo_lost item, two randomized loot items (loot4 and loot3 that are replaced with actual items), and lost documents. After placing the treasure, the global coordinate variables are reset to zero. This task does nothing if the treasure coordinates are not set (treasureTCX is 0). This is used as part of the treasure map quest system.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "replaceTriggerWithZoneSeries",
      "uses": [
        {
          "description": "Replaces a triggered area with content from a zone that's part of a series. This loads a zone whose name is formed by combining the base zone ID with a numeric suffix stored in a global variable, merges it into the current zone at the trigger location, then increments the global variable for the next use. Optionally applies terrain replacers and item removers to the loaded zone before merging. This is useful for progressive events where each trigger reveals the next stage of a zone series.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The base zone ID for the series. The actual zone loaded will be this ID plus a numeric suffix obtained from a global variable with the same name. For example, if this is 'cave' and the global variable 'cave' is 2, it loads zone 'cave2'. After merging, the global variable is incremented."
            }
          ],
          "optional": [
            {
              "name": "strings[1]",
              "description": "Level data string for terrain replacers and item removers. If provided, this is applied to the loaded zone before it's merged into the current zone. This allows customization of the merged content."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setupPartyFromGVars",
      "uses": [
        {
          "description": "Reconstructs the player's party from actor IDs or indices stored in global variables. This clears the current party and rebuilds it by looking up each global variable specified in the string list. If a global variable contains a number, it's treated as an index into the existing party actors list. If it contains text, it's treated as an actor ID to find in the current zone. This is useful for restoring a specific party configuration that was saved to global variables.",
          "required": [
            {
              "name": "strings[0+]",
              "description": "Global variable names to look up. Each string is the name of a global variable that contains either a numeric party index or an actor ID string. The party is rebuilt in the order these global variables are specified. Multiple strings can be provided to build a party with multiple members."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "healfaction",
      "uses": [
        {
          "description": "Fully heals all actors in the current zone that belong to a specific faction. This restores HP and MP to maximum for all actors matching the specified faction. The healing does not restore max HP values (only heals up to current max). This is useful for scenario setup or event scripting where you need to ensure a faction starts at full health.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The faction ID of actors to heal. All actors in the current zone with this faction will be fully healed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeTempPartyActors",
      "uses": [
        {
          "description": "Removes all temporary party members from the party and related lists. This removes any party actors that cannot be fully controlled, cleaning them from the active party, the missing party list, and the overboard party list. Permanent party members (those with full control) are retained. This is used to clean up temporary companions or guests when they should no longer be with the party.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "removeExcessLandingPartyActors",
      "uses": [
        {
          "description": "Removes party actors from the zone who are not in the designated landing party. This checks the landing party list and removes any actors from the zone who aren't included in it. This is used when transitioning to areas where only specific party members should be present, such as landing parties for ship-to-shore missions.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "getItemsFromZone",
      "uses": [
        {
          "description": "Retrieves all items that can be picked up from a specified zone and adds them to a location in the current zone. This loads the target zone, collects all pickable items from it, and places them at the specified tile coordinate in the current zone. The items are removed from the source zone. This is useful for collecting loot or items from areas without actually visiting them.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The zone ID from which to retrieve items. This zone is loaded temporarily, its pickable items are collected, and then it's unloaded."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The location to place the retrieved items in the current zone."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "location_tryUnlockItems",
      "uses": [
        {
          "description": "Attempts to unlock items at the current location. This calls the unlock items method on the current location, which typically checks if requirements are met to unlock special items or rewards at that location. This is part of the location reward and progression system.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "resetFleetAI",
      "uses": [
        {
          "description": "Resets the AI state for all fleets in the game. This clears waypoint destinations, pathfinding data, and AI variables for every fleet's flagship, forcing them to recalculate their behavior from scratch. This is useful for resolving stuck fleets or resetting naval AI after major game state changes.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "adjustFleetAlliegances",
      "uses": [
        {
          "description": "Converts actors marked as escorts into hostile enemies when an enemy fleet is present. This searches for actors with the same faction as the enemy fleet who are marked with combat label 'E' (escort), then changes them to hostile enemies with red exclamation marks. It also clears the 'playerEscort' global variable. This is used for scenarios where escort ships turn against the player.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "openJournal",
      "uses": [
        {
          "description": "Opens the journal interface and displays a specific journal entry. The journal opens to the specified entry index with both pages visible, no button cursor, and in a non-interactive display mode. If the journal is already open, it animates forward before showing the new entry. Optionally, all buttons on the journal can be hidden.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The index of the journal entry to display. This is cast to an integer and used to navigate to a specific entry in the player's known journal entries list."
            }
          ],
          "optional": [
            {
              "name": "bools[0]",
              "description": "Whether to hide all buttons on the journal interface. When true, the journal is displayed in a read-only mode with no interactive buttons. When false or not provided, buttons display normally."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "openJournal_interactive",
      "uses": [
        {
          "description": "Opens the journal interface in interactive mode and displays a specific journal entry. Unlike the non-interactive 'openJournal' task, this version allows full player interaction with the journal including navigation and button clicks. If the journal is already open, it animates forward before showing the new entry.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The index of the journal entry to display. This is cast to an integer and used to navigate to a specific entry in the player's known journal entries list."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "closeJournal",
      "uses": [
        {
          "description": "Closes the journal interface if it's currently open. This refreshes the journal state and sets it to not visible, removing it from the screen.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "learnJournalEntry",
      "uses": [
        {
          "description": "Teaches the player a new journal entry and displays it in the journal. The entry is added to the player's known entries with an optional alert notification. By default, after learning the entry, the journal animates and opens to display the new entry in non-interactive mode. This automatic display can be suppressed with the second bool parameter.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The journal entry ID to learn. This should match a journal entry defined in the game data. The entry is added to the player's known journal entries."
            },
            {
              "name": "bools[0]",
              "description": "Whether to show an alert notification when the journal entry is learned. When true, displays a notification to the player that a new entry was added."
            }
          ],
          "optional": [
            {
              "name": "bools[1]",
              "description": "Whether to suppress the automatic journal opening and display. When true, the journal does not automatically open to show the new entry. When false or not provided, the journal opens and animates to display the newly learned entry."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "moveJournal",
      "uses": [
        {
          "description": "Animates the journal window to a new screen position over a specified duration. The destination position can be specified as absolute coordinates from a tile coordinate, or can be mirrored from the right edge of the screen, or centered on screen. The journal smoothly tweens from its current position to the destination.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The duration of the movement animation in seconds. The journal will smoothly tween from its current position to the destination over this time period."
            },
            {
              "name": "bools[0]",
              "description": "Whether to mirror the X position from the right edge of the screen. When true, the X coordinate is measured from the right edge instead of the left, allowing for right-aligned positioning."
            },
            {
              "name": "bools[1]",
              "description": "Whether to center the journal on screen. When true, ignores the tile coordinate position and centers the journal both horizontally and vertically on the screen. This takes precedence over the mirror setting."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The destination coordinates for the journal window position. The X and Y values from this tile coordinate are used as the target screen position in pixels. This position can be modified by the bool parameters."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "addJournalGoal",
      "uses": [
        {
          "description": "Adds a new goal to the player's journal quest tracker. Goals appear in the journal as active objectives. Multiple string arguments are combined with commas to form the complete goal text. The goal is added to the first available goal slot (goal0 through goal19). If the goal already exists, it is not added again. When a new goal is added, the journal opens with a visual flash and sound effect to highlight the new entry.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The first part of the goal text. This is the base description or main objective of the goal."
            }
          ],
          "optional": [
            {
              "name": "strings[1+]",
              "description": "Additional parts of the goal text. Each additional string is appended to the goal text separated by commas. Empty strings are skipped. This allows building complex, multi-part goal descriptions from separate components."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "completeJournalGoal",
      "uses": [
        {
          "description": "Marks a journal goal as completed by drawing a line through it in the quest tracker. The goal text must exactly match an existing active goal (including any comma-separated components). When completed, the journal opens with a visual flash and completion sound effect. By default, completing a goal awards 50 XP to all crew members and adds a brief interaction delay. The XP reward and delay can be suppressed with a bool parameter.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The first part of the goal text to complete. This must match the first part of an existing active goal."
            }
          ],
          "optional": [
            {
              "name": "strings[1+]",
              "description": "Additional parts of the goal text to complete. Each string is appended with commas to form the complete goal identifier. The assembled text must exactly match an existing goal to be marked complete."
            },
            {
              "name": "bools[0]",
              "description": "Whether to suppress the XP reward and interaction delay. When true, the goal is marked complete without awarding XP or adding delay. When false or not provided, awards 50 XP to all crew and adds a 0.6 second interaction delay."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setTerrainType_spriteOffsetMult",
      "uses": [
        {
          "description": "Sets the sprite offset multipliers for a specific terrain type. These multipliers affect how the terrain sprite is positioned relative to its tile, allowing for visual adjustments and variations in terrain rendering. This is a debug/development tool for tuning terrain appearance.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The terrain type ID to modify. This identifies which terrain type's sprite offset multipliers should be changed."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The sprite offset multiplier values. The X coordinate sets the horizontal sprite offset multiplier, and the Y coordinate sets the vertical sprite offset multiplier for the terrain type."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setTerrainType_spriteOffsetModulo",
      "uses": [
        {
          "description": "Sets the sprite offset modulo values for a specific terrain type. These modulo values create variation in sprite positioning by applying a modulo operation to tile coordinates, allowing terrain sprites to have controlled positional variation across tiles. This is a debug/development tool for creating visual variety in terrain.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The terrain type ID to modify. This identifies which terrain type's sprite offset modulo values should be changed."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The sprite offset modulo values. The X coordinate sets the horizontal sprite offset modulo, and the Y coordinate sets the vertical sprite offset modulo for the terrain type."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "setTerrainTypes_spriteOffsetModulo",
      "uses": [
        {
          "description": "Sets the sprite offset modulo values for all terrain types whose IDs start with a specific prefix. This allows bulk modification of multiple related terrain types at once. The modulo values create variation in sprite positioning across tiles, useful for creating visual variety in terrain rendering. This is a debug/development tool for batch-updating terrain appearance.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The terrain type ID prefix. All terrain types whose IDs start with this string will have their sprite offset modulo values updated. For example, 'grass' would affect 'grass1', 'grass2', 'grassland', etc."
            }
          ],
          "optional": [
            {
              "name": "tileCoords[0]",
              "description": "The sprite offset modulo values to apply. The X coordinate sets the horizontal sprite offset modulo, and the Y coordinate sets the vertical sprite offset modulo for all matching terrain types."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "discoverFog",
      "uses": [
        {
          "description": "Reveals fog of war around the task's assigned actor at their current position. This calls the fog discovery function multiple times (4 times) to ensure a thorough reveal of the surrounding area. This is typically used when entering a new area or when an event should reveal the nearby terrain.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setFog",
      "uses": [
        {
          "description": "Directly sets the fog of war state (known/unknown) for a square area of tiles. This can reveal or hide areas in the fog of war without requiring actor movement or line of sight. The area is centered on the tile coordinate from the task and extends outward by the specified radius. Optionally, this can modify fog of war for a different zone than the current one.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The radius of the square area to modify. The fog state is set for all tiles in a square from (center - radius) to (center + radius). This is cast to an integer."
            },
            {
              "name": "bools[0]",
              "description": "The fog state to set. When true, tiles are marked as known (revealed). When false, tiles are marked as unknown (hidden/fogged)."
            }
          ],
          "optional": [
            {
              "name": "strings[0]",
              "description": "The zone ID for which to modify fog of war. If provided and not empty, modifies fog for that zone instead of the current zone. This allows modifying fog in zones the player isn't currently in. If empty or not provided, uses the current zone."
            },
            {
              "name": "tileCoords[0]",
              "description": "The center point of the fog modification area. The fog of war state will be changed for tiles around this coordinate."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "resetAllTileReactTimers",
      "uses": [
        {
          "description": "Resets the reaction timers for all tiles in the current zone. Tiles track when they were last reacted with (for elemental reactions, effects, etc.) to prevent rapid repeated reactions. This task clears all those timers, allowing tiles to react immediately again. This is useful for resetting zone state or enabling repeated environmental effects.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "changePlayerActor",
      "uses": [
        {
          "description": "Changes which party member is the currently controlled player character. This switches control to the specified actor without any additional side effects like reordering the party, playing effects, or bringing other actors forward. The change is immediate and minimal.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor ID of the party member to make the new player-controlled character. This actor must be in the current party."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setGlobalVarToActorProperty",
      "uses": [
        {
          "description": "Reads a property or field value from an actor and stores it in a global variable as a string. This allows extracting actor data (position, stats, state, etc.) and saving it for later use in scripts or conditions. The property is accessed via reflection, so any public field or property of the Actor class can be retrieved.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to set. The actor's property value will be stored in this global variable as a string."
            },
            {
              "name": "strings[1]",
              "description": "The actor ID whose property should be read. This identifies which actor in the current zone to get data from."
            },
            {
              "name": "strings[2]",
              "description": "The property or field name to read from the actor. This should be the name of a public property or field on the Actor class (e.g., 'HP', 'positionTC', 'faction', etc.). The value is retrieved via reflection."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "testzones",
      "uses": [
        {
          "description": "Debug command that automatically travels through multiple zones for testing purposes. This generates a list of zone files that don't match known level data prefixes (excluding special zones like procedural zones starting with 'sp', 'grove', 'den', 'bz'), then creates a series of timed travel tasks to visit them sequentially. Each zone visit shows a counter on screen. After queuing all travel tasks, the game switches to travel mode. This is useful for batch-testing zones or creating automated zone tours.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The maximum number of zones to visit per iteration. If not provided, all discovered zones are visited. This can be used to limit the test run to a specific number of zones."
            },
            {
              "name": "floats[1]",
              "description": "The number of times to iterate through the zone list. Each iteration visits up to the maximum number of zones. If not provided, defaults to 1 iteration. This is cast to an integer."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "stopCombatAIActing",
      "uses": [
        {
          "description": "Immediately stops the current AI-controlled actor's turn in combat and disables further AI processing. This ends the current AI actor's turn and prevents the AI system from processing any more actions until re-enabled. This is useful for interrupting AI behavior during combat, such as when transitioning to a cutscene or special event. This only has an effect if combat mode is active and an uncontrollable (AI) actor is currently acting.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "startCombatAIActing",
      "uses": [
        {
          "description": "Re-enables AI processing in combat. This allows the AI system to continue processing turns for AI-controlled actors. This is typically used after stopCombatAIActing or another event that disabled AI processing, to resume normal combat behavior.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "relocateActors",
      "uses": [
        {
          "description": "Moves actors away from a specific location to nearby positions outside a defined radius. This finds all actors within a radius of a center point, then relocates them to occupiable tiles just outside that radius. Optionally filters by faction and can trigger pain animations. This is useful for clearing an area of actors or creating space around a specific location. If party actors are relocated, their movement paths are cleared.",
          "required": [],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The radius in tiles around the center point. Actors within this radius are relocated to positions at least this far from the center. If not provided, defaults to 1 tile. The value is cast to an integer."
            },
            {
              "name": "strings[0]",
              "description": "The faction to filter by. If provided, only actors of this faction are relocated. If empty or not provided, all actors within the radius are relocated regardless of faction."
            },
            {
              "name": "bools[0]",
              "description": "Whether to play a pain animation when relocating actors. When true, each relocated actor plays their pain animation as they are moved, providing visual feedback of the displacement."
            },
            {
              "name": "tileCoords[0]",
              "description": "The center point from which actors should be relocated. Actors within the specified radius of this coordinate will be moved to positions outside the radius."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "relocateActors_point",
      "uses": [
        {
          "description": "Relocates actors to specific travel points in the zone. This finds all actors within a radius of a center coordinate (optionally filtered by faction), then distributes them to available travel point items in the zone. The actors are placed on travel points matching a specific set number. This is useful for teleporting groups of actors to designated spawn points or positioning areas.",
          "required": [
            {
              "name": "bools[0]",
              "description": "First boolean parameter passed to the actor placement function, controlling some aspect of how actors are positioned on travel points."
            },
            {
              "name": "bools[1]",
              "description": "Second boolean parameter passed to the actor placement function, controlling another aspect of how actors are positioned on travel points."
            }
          ],
          "optional": [
            {
              "name": "floats[0]",
              "description": "The radius in tiles to search for actors around the center point. If not provided, defaults to 1 tile. The value is cast to an integer."
            },
            {
              "name": "strings[0]",
              "description": "The faction to filter by. If provided, only actors of this faction are relocated. If empty or not provided, all actors within the radius are candidates for relocation."
            },
            {
              "name": "strings[1]",
              "description": "The travel point set number. Actors are placed on travel point items matching this set number. If not provided, defaults to 0. This is parsed as an integer."
            },
            {
              "name": "tileCoords[0]",
              "description": "The center point for finding actors to relocate. All actors within the specified radius of this coordinate are candidates for relocation."
            }
          ]
        }
      ],
      "aliases": []
    },
    {
      "name": "placeActorFromBoardedShip",
      "uses": [
        {
          "description": "Extracts a specific crew member from a boarded enemy ship and places them in the zone. This searches the crew of the currently boarded target ship for an actor matching a specific actor type ID, then places that actor at a designated travel point and adds them to the active zone. This is used during boarding scenarios to bring specific enemy crew members into combat or interaction.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The actor type ID of the crew member to extract from the boarded ship. The first crew member matching this type ID will be placed in the zone at the specified travel point."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setupLocationAsPlayerPort",
      "uses": [
        {
          "description": "Converts the current location into the player's home port with all associated facilities and services. This comprehensive setup adds port functionality, shipwright services (selling cog, caravel, and pinnace boats), cafe service with basic food, hiring services for humans and scurio, a home icon, quest generation, location stash with starting supplies (lifeshards, revive dust, splints, shovel, XP gems, random action tablets), and an unlockable ability crest. If a player faction is set, the port becomes everloyal to that faction. This is typically used when the player establishes their base of operations.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addIconToCurrentLocation",
      "uses": [
        {
          "description": "Adds a tooltip icon to the current location's icon list. These icons appear on the location to indicate available services or features (such as training, shops, quests, etc.). The icon is appended to the end of the existing icon list.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The icon ID to add to the location. This should be a valid tooltip icon identifier (e.g., 'sign_icon_book' for training, 'sign_icon_muscle' for combat training, etc.)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setPartyActorAsVendor",
      "uses": [
        {
          "description": "Converts a party member into an NPC vendor at a specific location. This removes the actor from the party (without adding them to barracks), makes them non-swappable, sets their dialog node, marks them as a reserved actor, and places them at a designated travel point. If another actor with the same dialog role already exists at that location, they swap places (the old vendor returns to the party and the new vendor takes their position). This is used for setting up party members as shopkeepers, quest givers, or other service NPCs.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The index of the party actor to convert into a vendor. This is an index into the current party actors list, cast to an integer. If the party doesn't have enough members (index out of range), the task does nothing."
            },
            {
              "name": "floats[1]",
              "description": "The travel point number where the vendor should be positioned. The actor is placed at this travel point location (with set number 0). Cast to an integer."
            },
            {
              "name": "strings[0]",
              "description": "The dialog node ID for the vendor. This determines what dialog tree the vendor uses when interacted with (e.g., 'questgiver' for quest givers, shop dialog IDs for merchants, etc.)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "readdVendorToParty",
      "uses": [
        {
          "description": "Converts an NPC vendor back into a party member. This finds an actor at a specified travel point who has a specific dialog role, clears their dialog assignment, and adds them back to the party. The actor is then moved to a position near the player. This is the reverse of setPartyActorAsVendor, used to reclaim party members who were assigned as vendors.",
          "required": [
            {
              "name": "floats[0]",
              "description": "The travel point number where the vendor is located. The task looks for an actor at this travel point (with set number 0) to convert back into a party member. Cast to an integer."
            },
            {
              "name": "strings[0]",
              "description": "The dialog node ID that identifies the vendor to remove. Only actors with this exact dialog node ID will be converted back to party members. This ensures the correct vendor is targeted."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "askForNewPortName",
      "uses": [
        {
          "description": "Prompts the player to name a port by opening a text input dialog. This switches to travel mode and displays a confirmation dialog asking 'What will you name the port?' with text input enabled. The dialog connects to the 'renamePort' confirmation handler and cannot be closed by the player until they provide a name. This is used when establishing a new port or allowing the player to customize their home port's name.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "setupCafeFoodDialogOptionsWealth100",
      "uses": [
        {
          "description": "Dynamically generates dialog options for a cafe food menu based on discovered food items. This clears the specified dialog node's options and rebuilds them with available food choices. It always includes basic food, then adds special regional foods discovered at other locations (with specific wealth requirements). Each option shows the food item with its cost, description, and effects. Selecting an option changes the cafe's food offering. This creates a dynamic menu system where discovering new locations unlocks new food options at the player's port cafe.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID whose options should be populated with cafe food choices. This dialog node will have its options cleared and replaced with dynamically generated food menu items."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "changeCafeFood",
      "uses": [
        {
          "description": "Changes the cafe food offering at the current location. This can either set the cafe food to a specific item type ID, or copy the cafe food and hiring services from another location. When given a location ID, it copies both the cafe food type and the auto-spawn hiring options from that location to the current location. When given an item type ID (if no matching location exists), it simply sets the cafe food to that item.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The location ID or item type ID to use. If this matches a location ID, the current location's cafe food and hiring services are copied from that location. If no location matches, this is treated as an item type ID and the cafe food is set to that item directly."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "giveMathXPToActorWithDialogNodeID",
      "uses": [
        {
          "description": "Awards XP to all actors in the zone with a specific dialog node ID based on a formula calculation. The XP amount is calculated from a formula and capped so the recipient doesn't exceed the main player's total XP earned. This is useful for keeping NPC levels synchronized with the player. The XP is awarded silently without visual feedback, ignoring tired penalties and party size modifiers.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The dialog node ID to match. All actors in the current zone with this dialog node ID will receive XP. This is typically used to identify specific NPCs like trainers, vendors, or quest givers."
            },
            {
              "name": "strings[1]",
              "description": "The formula string to calculate the XP amount. This formula is evaluated to determine how much XP to award. The result is capped to ensure the recipient doesn't exceed the main player's total XP, and negative values are floored at 0."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "convertPlayerPortsToFaction",
      "uses": [
        {
          "description": "Converts all player-owned ports in the overworld to a specific faction. This searches for all locations on the overworld map ('eral') that are tagged as player ports ('sport') and sets them to be everloyal to the specified faction. This is used when the player aligns with or establishes a faction, making all their ports loyal to that faction.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The faction ID to convert player ports to. All player ports will be set as everloyal to this faction, affecting their diplomatic relationships and behavior."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "addLocationUnlockable",
      "uses": [
        {
          "description": "Adds an unlockable item to the current location's unlock system. Unlockable items become available for purchase or access when certain conditions (typically wealth/prosperity level) are met. After adding the unlockable, this task attempts to unlock any items whose conditions are now satisfied. This is used to expand a location's available goods and services as it develops.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The item type ID of the item to add as an unlockable. This is what will become available at the location when conditions are met."
            },
            {
              "name": "floats[0]",
              "description": "The quantity or amount of the unlockable item. This determines how many of the item are available when unlocked."
            },
            {
              "name": "strings[1]",
              "description": "The formula string that defines the unlock condition. This formula is evaluated to determine when the item should become available (e.g., 'm:locWealth(90)' requires location wealth of 90 or higher)."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "task_gvar",
      "uses": [
        {
          "description": "Executes a task whose name is stored in a global variable. This retrieves a global variable's string value and, if it's not '0', treats that value as a task name and executes it. This allows dynamic task execution where the specific task to run is determined by game state stored in global variables. This is useful for branching scenarios where different tasks should execute based on player choices or game conditions.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The name of the global variable to read. The value of this global variable (if not '0') is used as the task name to execute. The task name should be a valid task type that can be constructed and executed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "dominateworld",
      "uses": [
        {
          "description": "Debug command that instantly makes all ports in the world wealthy and loyal to the player's faction. This sets every port location in the overworld to maximum wealth (100) and, if a player faction is defined, makes them all everloyal to that faction. This is a cheat command for testing or achieving instant world domination.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "gc",
      "uses": [
        {
          "description": "Debug command that forces garbage collection to free memory. This clears extra zones from memory and runs the .NET garbage collector twice with finalization between runs. This is useful for freeing up memory during development or when experiencing memory issues. The aggressive collection pattern ensures thorough cleanup of managed resources.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "clear",
      "uses": [
        {
          "description": "Debug command that clears all debug display queues and on-screen debug strings. This removes all debug visualizations, indefinite debug drawings, and darkness-ignoring draw elements from the screen. This is useful for cleaning up debug displays during development.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "debug_drawStrings",
      "uses": [
        {
          "description": "Debug command that displays a labeled string on screen. This shows a debug message with a category label and content, similar to debugMessage but with a different implementation. The message persists on screen for debugging purposes. If either parameter is missing or the first string is empty, the command does nothing.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The category label for the debug message. This must not be empty for the message to display. The label identifies what the debug information represents."
            },
            {
              "name": "strings[1]",
              "description": "The content of the debug message to display on screen. This is the actual debug information shown alongside the category label."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "loadItOnUpYo",
      "uses": [
        {
          "description": "Loads and transitions to a new zone with specific camera positioning. This moves the game to a different zone, placing the camera at specified coordinates with defined width and height parameters. This is a zone transition command that provides control over the initial camera view in the new zone.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The zone ID to load and transition to. This should be a valid zone identifier from the game's zone data."
            },
            {
              "name": "floats[0]",
              "description": "The camera width parameter for the new zone. This affects the horizontal extent of the camera view when entering the zone."
            },
            {
              "name": "floats[1]",
              "description": "The camera height parameter for the new zone. This affects the vertical extent of the camera view when entering the zone."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "maximizeWindowSet",
      "uses": [
        {
          "description": "Sets the game window's maximized state. This either maximizes the window to fill the screen or restores it to windowed mode, depending on the boolean parameter. This provides control over the window display mode.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to maximize the window. When true, the window is maximized to full screen. When false, the window is restored to its previous windowed size."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "maximizeWindowIfTrue",
      "uses": [
        {
          "description": "Conditionally maximizes the window and hides UI elements if the boolean parameter is true. When activated, this maximizes the window, hides the main menu background, and hides the UI manager. This is useful for entering a clean full-screen mode for specific scenarios like cutscenes or presentations.",
          "required": [
            {
              "name": "bools[0]",
              "description": "Whether to maximize and clean the display. When true, maximizes the window and hides the main menu background and UI manager. When false, does nothing."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "changeScreenResolution2",
      "uses": [
        {
          "description": "Triggers a screen resolution change using the game's resolution change system. This calls the resolution change method which applies any pending resolution changes that were previously queued. This is typically used after setting a target resolution with other commands.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "uploadmod",
      "uses": [
        {
          "description": "Uploads a new mod to the Steam Workshop. This validates the specified mod folder, then initiates the Steam Workshop upload process by creating a new Workshop item. A confirmation dialog appears during the upload process, and user interaction is blocked until the upload completes. This is used to publish new mods to Steam Workshop for the first time.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The mod folder name to upload. This folder must exist in the mod data directory and pass validation checks. The folder should contain the mod content and a preview image with the same name as the folder."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "getmod",
      "uses": [
        {
          "description": "Sets the current mod ID for subsequent mod operations. This parses a Steam Workshop published file ID from a string and stores it for use with other mod-related tasks like editing or viewing. If the string cannot be parsed as a valid ID, sets the mod ID to 0. This is used to target a specific Workshop item for editing or management.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The Steam Workshop published file ID as a string. This should be the numeric ID of a Workshop item. If this cannot be parsed as a valid unsigned long integer, the mod ID is set to 0."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "editmod",
      "uses": [
        {
          "description": "Updates an existing mod on the Steam Workshop. This validates the mod folder, then initiates a Steam Workshop update for the previously set mod ID (from getmod task). The update includes the mod content and preview image. A confirmation dialog appears during upload and user interaction is blocked until complete. This is used to publish updates to existing Workshop mods.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The mod folder name to upload as an update. This folder must exist in the mod data directory and pass validation. The update will be applied to the Workshop item ID previously set by the getmod task."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "editmodtags",
      "uses": [
        {
          "description": "Updates only the tags for an existing Steam Workshop mod. This concatenates all provided strings, parses them as tags, and updates the tags for the previously set mod ID (from getmod task) without changing the mod content. A confirmation dialog appears during the upload process. This is useful for updating mod categorization and discoverability without re-uploading content.",
          "required": [
            {
              "name": "strings[0+]",
              "description": "Tag strings to apply to the mod. All provided strings are concatenated together and parsed as a tag list. The tags control how the mod is categorized and discovered on Steam Workshop."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "loadModPageInBrowser",
      "uses": [
        {
          "description": "Opens the Steam Workshop page for the current mod in the default web browser. This launches the browser to view the Workshop page for the mod ID previously set by the getmod task. This is useful for viewing or managing a Workshop item after uploading or editing.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "test",
      "uses": [
        {
          "description": "Debug test task that performs various test operations. This schedules a follow-up test task and demonstrates regex replacement operations for testing purposes. This is a development/debugging task with no meaningful gameplay effect.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "UIPartInputCommand",
      "uses": [
        {
          "description": "Handles UI part input commands with full parameters. This processes UI input commands with a complete parameter set including multiple strings and floats. The exact behavior depends on the specific UI command being handled by the input manager.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The first string parameter for the UI command. This typically identifies the UI element or command type."
            },
            {
              "name": "strings[1]",
              "description": "The second string parameter for the UI command. This typically provides additional context or data for the command."
            },
            {
              "name": "floats[0]",
              "description": "The first float parameter for the UI command. This typically provides numeric input or configuration data."
            },
            {
              "name": "strings[2]",
              "description": "The third string parameter for the UI command. This provides further specification or data for the command."
            },
            {
              "name": "floats[1]",
              "description": "The second float parameter for the UI command. This provides additional numeric input or configuration data."
            }
          ],
          "optional": []
        },
        {
          "description": "Handles simple UI part input commands with minimal parameters. This processes UI input commands with just a single string identifier. The exact behavior depends on the specific UI command being handled by the input manager.",
          "required": [
            {
              "name": "strings[0]",
              "description": "The UI command identifier. This specifies which UI input command should be executed."
            }
          ],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "noSpeechFXLookOrAnim",
      "uses": [
        {
          "description": "Empty task that performs no operations. This task type exists but has no implementation. It may be used as a placeholder or flag in the task system.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    },
    {
      "name": "test2",
      "uses": [
        {
          "description": "Empty test task that performs no operations. This is a test/debug task type with no implementation. It may be used for testing the task system itself.",
          "required": [],
          "optional": []
        }
      ],
      "aliases": []
    }
  ]
}
